import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";

import EliminationBracket from "./EliminationBracket";
import FieldHint from "./FieldHint";
import { playoffQualificationSummary } from "../lib/playoffQualificationSummary";
import TeamPlayFormatSection from "./TeamPlayFormatSection";
import SpecialScoringSection from "./SpecialScoringSection";
import { normalizeSpecialScoring, specialScoringError } from "../lib/specialScoring";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { teamPlayValidationError } from "../lib/teamPlay";
import type { TeamPlayConfig, TiebreakPreset } from "../lib/api";
import {
  eliminationFormatPreview,
  eliminationPlanForBracket,
  roundRobinFormatPreview,
  suggestedBracketSize,
} from "../lib/formatPreview";
import { tournamentTypeLabel } from "../lib/playerLabels";
import { roundRobinDefaultsForEnrolled } from "../lib/roundRobinDefaults";
import ConsolationConfig, { DEFAULT_CONSOLATION, consolationSummary } from "./ConsolationConfig";
import {
  CLOSE_HELP,
  DEFAULT_STANDINGS_POINTS,
  DETERMINISTIC_CLOSE_NOTE,
  H2H_HELP,
  itfLadderSteps,
  MOVABLE_TIEBREAKER_OPTIONS,
  RECORD_HELP,
  TIEBREAKER_HELP,
  TIEBREAKER_LABELS,
  TIEBREAKER_SCOPE_NOTE,
  customStandingsRules,
  itfStandingsRules,
  movableTiebreakers,
  moveTiebreaker,
  pointsLeagueStandingsRules,
  standingsPointsRelationError,
  standingsRulesKind,
  type StandingsRulesKind,
} from "../lib/standingsRules";
import {
  api,
  ApiError,
  type EliminationFormatConfig,
  type LadderFormatConfig,
  type LadderDropoutPolicy,
  type LadderFrequency,
  type LadderSetFormat,
  type SetGameType,
  type LadderPlayoffRoundEntry,
  type LadderPlayoffRoundRule,
  type LadderRankingScope,
  type LadderWeeklyPattern,
  type LadderWeeklyPatternEntry,
  type RoundRobinBracketPolicy,
  type RoundRobinSeedMethod,
  type RoundRobinFormatConfig,
  type RoundRobinFormatMode,
  type RoundRobinGroupScheduleType,
  type RoundRobinQualificationRule,
  type StandingsRulesConfig,
  type TournamentFormatConfig,
  type TournamentFormatConfigJson,
  type TournamentType,
} from "../lib/api";

const FREQUENCY_DURATIONS: Record<LadderFrequency, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  custom: 7,
};

const RANKING_SCOPE_LABELS: Record<LadderRankingScope, string> = {
  group_current_rank: "Ranking actual del grupo",
  group_initial_rank: "Ranking inicial del grupo",
  manual: "Manual",
};

const DROPOUT_POLICY_LABELS: Record<LadderDropoutPolicy, string> = {
  manual_default: "Baja con confirmación",
  manual_reassign: "Reacomodar manualmente",
  bye: "Dejar BYE",
  cancel_matchup: "Cancelar enfrentamiento",
};

const SET_FORMAT_OPTIONS: Array<{ value: LadderSetFormat; label: string }> = [
  { value: "one_set", label: "1" },
  { value: "two_sets_super_tiebreak", label: "2 y Super Muerte" },
  { value: "best_of_3", label: "2 de 3" },
  { value: "best_of_5", label: "3 de 5" },
];

const ROUND_ROBIN_FORMAT_MODE_LABELS: Record<RoundRobinFormatMode, string> = {
  round_robin_only: "Solo grupos",
  round_robin_playoffs: "Grupos + playoffs",
};

// Eduardo 2026-09-24: "Política de bracket" no se entendía. Es qué pasa
// cuando los clasificados no llenan un cuadro exacto (4, 8, 16…).
const ROUND_ROBIN_BRACKET_POLICY_LABELS: Record<RoundRobinBracketPolicy, string> = {
  auto_preliminary: "ronda preliminar entre los últimos clasificados",
  auto_byes: "pase directo (BYE) a los mejores clasificados",
  manual: "cuadro armado a mano en el Draw",
};

const ROUND_ROBIN_GROUP_SCHEDULE_LABELS: Record<RoundRobinGroupScheduleType, string> = {
  full_round_robin: "Todos contra todos",
  limited_schedule: "Calendario limitado",
  manual_schedule: "Manual",
};

const DEFAULT_ROUND_ROBIN_QUALIFICATION_RULES: RoundRobinQualificationRule[] = [
  { type: "top_n_per_group", positions_from: 1, positions_to: 2 },
  { type: "best_n_by_group_position", group_position: 3, count: 1 },
];

const SET_GAME_TYPE_OPTIONS: Array<{ value: SetGameType; label: string; helper: string }> = [
  { value: "normal_6", label: "Normal 6 games", helper: "Set tradicional a 6 juegos" },
  { value: "mini_4", label: "Mini set 4 games", helper: "Infantiles / partidos cortos" },
];

const SET_GAME_TYPE_LABELS: Record<SetGameType, string> = SET_GAME_TYPE_OPTIONS.reduce(
  (labels, option) => ({ ...labels, [option.value]: option.label }),
  {} as Record<SetGameType, string>,
);

const SET_TIEBREAK_GAME_OPTIONS = [4, 5, 6, 7, 8, 9, 10] as const;
const SUPER_TIEBREAK_POINTS_OPTIONS = [10] as const;

type ScoringConfig = Pick<
  LadderFormatConfig,
  "set_game_type" | "with_advantage" | "set_tiebreak_games" | "third_set_super_tiebreak_points"
>;

function thirdSetLabel(points: number | null): string {
  return points === null ? "3er set no aplica" : `3er set súper muerte a ${points} pts`;
}

// Eduardo 2026-09-15: los campos de formato de sets (# sets, tipo de set,
// tie-break, 3er set, ventaja) NO se renderizan en el Paso 5 — la captura de
// resultados es genérica (hasta 5 sets, tie-break y súper muerte por set) y
// el formato se describe en el reglamento. Los valores guardados siguen
// alimentando el resumen y el marcador default de las series. (Antes iban
// con el atributo hidden y el smoke los seguía encontrando en el DOM.)
const SHOW_SET_FORMAT_FIELDS: boolean = false;

function scoringSummary(config: ScoringConfig): string {
  // Eduardo 2026-09-15: el formato es informativo (convocatoria/reglamento y
  // marcador default); la captura de resultados acepta cualquier marcador
  // (hasta 5 sets, tie-break o súper muerte por set).
  // Eduardo 2026-09-24: decir qué sí puede hacer el admin en Resultados.
  return `${SET_GAME_TYPE_LABELS[config.set_game_type]} · ${config.with_advantage ? "con ventaja" : "sin ventaja"} · tie-break en ${config.set_tiebreak_games} juegos · ${thirdSetLabel(config.third_set_super_tiebreak_points)} · informativo: no condiciona la captura; en Resultados el admin puede agregar o quitar sets y capturar tie-break en cualquier set`;
}

const WEEKLY_PATTERN_LABELS: Record<LadderWeeklyPattern, string> = {
  even_challenge_odd: "Pares retan a nones",
  odd_challenge_even: "Nones retan a pares",
  even_challenge_odd_offset: "Pares retan X lugares arriba a nones",
  odd_challenge_even_offset: "Nones retan X lugares arriba a pares",
  manual: "Manual",
};

const PLAYOFF_ROUND_RULE_LABELS: Record<LadderPlayoffRoundRule, string> = {
  top_6_preliminary: "Top 6 · Preliminar 3 vs 6 / 4 vs 5",
  top_6_semifinal_reseed: "Top 6 · Semifinales con 1º/2º + ganadores",
  top_4_semifinal: "Top 4 · Semifinal 1 vs 4 / 2 vs 3",
  top_8_quarterfinal: "Top 8 · Cuartos 1 vs 8 / 2 vs 7 / 3 vs 6 / 4 vs 5",
  semifinal_winners: "Semifinales · Ganadores de ronda previa",
  final_winners: "Final · Ganadores de semifinal",
  manual: "Manual",
};

const PLAYOFF_ROUND_RULE_HELPERS: Record<LadderPlayoffRoundRule, string> = {
  top_6_preliminary: "Descansan/directo 1º y 2º; juegan 3º vs 6º y 4º vs 5º.",
  top_6_semifinal_reseed: "1º y 2º entran contra los ganadores de preliminar según siembra.",
  top_4_semifinal: "Semifinal directa para cuatro clasificados.",
  top_8_quarterfinal: "Cuartos de final para ocho clasificados.",
  semifinal_winners: "Cruces entre ganadores de la ronda anterior.",
  final_winners: "Final entre ganadores de semifinal.",
  manual: "El admin define los cruces manualmente.",
};

const PLAYOFF_ROUND_RULE_OPTIONS = Object.keys(PLAYOFF_ROUND_RULE_LABELS) as LadderPlayoffRoundRule[];

const OFFSET_PATTERNS: ReadonlySet<LadderWeeklyPattern> = new Set([
  "even_challenge_odd_offset",
  "odd_challenge_even_offset",
]);

const DEFAULT_OFFSET = 3;
const OFFICIAL_PATTERN_WEEKS = 14;
const ELIMINATION_BRACKET_SIZES = [2, 4, 8, 16, 32, 64, 128, 256] as const;

function eliminationRoundCount(size: number): number {
  return Math.max(1, Math.log2(Math.max(2, size)));
}

// eliminationPlanForBracket vive en lib/formatPreview.ts (compartida con el
// preview visual del formato y cubierta por vitest).

function isOffsetPattern(pattern: LadderWeeklyPattern): boolean {
  return OFFSET_PATTERNS.has(pattern);
}

function defaultWeeklyPattern(week: number): LadderWeeklyPatternEntry {
  return {
    week,
    pattern: week % 2 === 1 ? "even_challenge_odd" : "odd_challenge_even",
    offset: null,
    bye: false,
  };
}

function resizeWeeklyPatterns(
  current: LadderWeeklyPatternEntry[] | undefined,
  roundCount: number,
): LadderWeeklyPatternEntry[] {
  const safe = Array.isArray(current) ? current : [];
  const next: LadderWeeklyPatternEntry[] = [];
  for (let i = 0; i < roundCount; i += 1) {
    const week = i + 1;
    const existing = safe[i];
    if (existing) {
      next.push({ ...existing, week });
    } else {
      next.push(defaultWeeklyPattern(week));
    }
  }
  return next;
}

function defaultPlayoffRoundRule(index: number, total: number, playerCount: number): LadderPlayoffRoundRule {
  if (index === total) return "final_winners";
  if (playerCount === 6 && total >= 3) {
    if (index === 1) return "top_6_preliminary";
    if (index === 2) return "top_6_semifinal_reseed";
  }
  if (playerCount === 8 && total >= 3) {
    if (index === 1) return "top_8_quarterfinal";
    if (index === 2) return "semifinal_winners";
  }
  if (playerCount === 4 && total >= 2 && index === 1) return "top_4_semifinal";
  if (total >= 2 && index === total - 1) return "semifinal_winners";
  return "manual";
}

function resizePlayoffRounds(
  current: LadderPlayoffRoundEntry[] | undefined,
  playoffRoundCount: number,
  regularRoundCount: number,
  playerCount: number,
): LadderPlayoffRoundEntry[] {
  const safe = Array.isArray(current) ? current : [];
  const next: LadderPlayoffRoundEntry[] = [];
  for (let i = 0; i < playoffRoundCount; i += 1) {
    const roundIndex = i + 1;
    const existing = safe[i];
    next.push({
      week: regularRoundCount + roundIndex,
      round_index: roundIndex,
      rule: existing?.rule ?? defaultPlayoffRoundRule(roundIndex, playoffRoundCount, playerCount),
    });
  }
  return next;
}

function playoffRoundHelper(rule: LadderPlayoffRoundRule): string {
  return PLAYOFF_ROUND_RULE_HELPERS[rule] ?? "Regla de playoff.";
}

function buildOfficial14WeekPattern(): LadderWeeklyPatternEntry[] {
  const entries: LadderWeeklyPatternEntry[] = [];
  for (let week = 1; week <= OFFICIAL_PATTERN_WEEKS; week += 1) {
    if ([1, 3, 5, 7, 9, 11, 13].includes(week)) {
      entries.push({
        week,
        pattern: "even_challenge_odd",
        offset: null,
        bye: false,
      });
    } else if ([2, 6, 10].includes(week)) {
      entries.push({
        week,
        pattern: "odd_challenge_even",
        offset: null,
        bye: true,
      });
    } else if ([4, 8, 12].includes(week)) {
      entries.push({
        week,
        pattern: "odd_challenge_even_offset",
        offset: DEFAULT_OFFSET,
        bye: false,
      });
    } else {
      entries.push({
        week,
        pattern: "odd_challenge_even",
        offset: null,
        bye: false,
      });
    }
  }
  return entries;
}

const FORMAT_OPTIONS: Array<{
  value: TournamentType;
  title: string;
  badge: string;
  description: string;
  illustration: JSX.Element;
}> = [
  {
    value: "round_robin",
    title: "Round Robin",
    badge: "Matriz",
    description:
      "Todos contra todos dentro del grupo, con matriz visual y enfrentamientos por jornadas.",
    illustration: (
      <svg className="format-illus format-illus--svg format-illus--rr-svg" viewBox="0 0 124 72" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="124" height="72" rx="10" fill="#164a7d" />
        <rect x="34" y="8" width="11" height="11" rx="2" fill="#d8f14e" />
        <rect x="48" y="8" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="62" y="8" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="76" y="8" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="34" y="22" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="48" y="22" width="11" height="11" rx="2" fill="#d8f14e" />
        <rect x="62" y="22" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="76" y="22" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="34" y="36" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="48" y="36" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="62" y="36" width="11" height="11" rx="2" fill="#d8f14e" />
        <rect x="76" y="36" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="34" y="50" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="48" y="50" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="62" y="50" width="11" height="11" rx="2" fill="rgba(255,255,255,0.22)" />
        <rect x="76" y="50" width="11" height="11" rx="2" fill="#d8f14e" />
      </svg>
    ),
  },
  {
    value: "elimination",
    title: "Eliminatoria",
    badge: "Bracket",
    description:
      "Bracket eliminatorio con seeding por ranking inicial, ranking actual o acomodo manual.",
    illustration: (
      <svg className="format-illus format-illus--svg format-illus--elim-svg" viewBox="0 0 124 72" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="124" height="72" rx="10" fill="#164a7d" />
        <rect x="14" y="10" width="26" height="7" rx="3" fill="#9fb4cc" />
        <rect x="14" y="24" width="26" height="7" rx="3" fill="#9fb4cc" />
        <rect x="14" y="40" width="26" height="7" rx="3" fill="#9fb4cc" />
        <rect x="14" y="54" width="26" height="7" rx="3" fill="#9fb4cc" />
        <rect x="48" y="17" width="26" height="7" rx="3" fill="#5f7fa3" />
        <rect x="48" y="47" width="26" height="7" rx="3" fill="#5f7fa3" />
        <rect x="82" y="32" width="26" height="8" rx="3" fill="#d8f14e" />
      </svg>
    ),
  },
  {
    value: "ladder",
    title: "Escalera",
    badge: "Ranking",
    description:
      "Enfrentamientos por ranking actual del grupo, sugeridos automáticamente y con publicación por enfrentamiento.",
    illustration: (
      <svg className="format-illus format-illus--svg format-illus--ladder-svg" viewBox="0 0 124 72" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="124" height="72" rx="10" fill="#164a7d" />
        <rect x="16" y="54" width="15" height="12" rx="3" fill="#9fb4cc" />
        <rect x="35" y="43" width="15" height="23" rx="3" fill="#9fb4cc" />
        <rect x="54" y="32" width="15" height="34" rx="3" fill="#9fb4cc" />
        <rect x="73" y="21" width="15" height="45" rx="3" fill="#9fb4cc" />
        <rect x="92" y="10" width="15" height="56" rx="3" fill="#d8f14e" />
      </svg>
    ),
  },
];

function isLadderConfig(
  format_type: TournamentType,
  json: TournamentFormatConfigJson,
): json is LadderFormatConfig {
  return format_type === "ladder" && typeof (json as LadderFormatConfig).frequency === "string";
}

function isRoundRobinConfig(
  format_type: TournamentType,
  json: TournamentFormatConfigJson,
): json is RoundRobinFormatConfig {
  return (
    format_type === "round_robin" &&
    typeof (json as RoundRobinFormatConfig).rounds_per_pair === "number"
  );
}

function isEliminationConfig(
  format_type: TournamentType,
  json: TournamentFormatConfigJson,
): json is EliminationFormatConfig {
  return (
    format_type === "elimination" &&
    typeof (json as EliminationFormatConfig).seeding_source === "string"
  );
}

// Eduardo 2026-10-13: el partido por el 3er lugar es opcional por categoría
// (caso torneo 71: semis + final, sin 3er lugar). Misma casilla compacta del
// asistente (form__checkbox--boxed) en Round Robin y en Eliminatoria.
const THIRD_PLACE_HINT =
  "Si lo marcas, los dos que pierden en semifinales juegan por el 3er lugar. Si no, el torneo termina con la final.";

function ThirdPlaceCheckbox({
  checked,
  disabled,
  onChange,
  className,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <label className={`form__checkbox form__checkbox--boxed fmt-third-place${className ? ` ${className}` : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="hint-label">
          Partido por el 3er lugar
          <FieldHint label="¿Qué es el partido por el 3er lugar?">{THIRD_PLACE_HINT}</FieldHint>
        </span>
      </span>
    </label>
  );
}

interface Props {
  token: string;
  tournamentId: number;
  divisionId?: number | null;
  initialConfig: TournamentFormatConfig;
  onChange?: (config: TournamentFormatConfig) => void;
  onSaved?: (config: TournamentFormatConfig) => void;
  submitLabel?: string;
  /** Barra inferior del asistente: si viene, sustituye al botón «Guardar» del
   * resumen (evita dos botones primarios). Sus botones deben ser
   * `type="submit" form="format-config-form"` para reutilizar las mismas
   * validaciones y confirmaciones. */
  renderActions?: (state: { saving: boolean }) => ReactNode;
  /** Bloque "por equipos" que la categoría ya trae desde Datos aunque todavía
   * herede el formato del torneo: el Paso 5 lo conserva al elegir RR/eliminatoria. */
  inheritedTeamPlay?: TeamPlayConfig | null;
  /** Inscritos vigentes de la categoría: de aquí salen los defaults de grupos
   * cuando el Formato todavía no se ha guardado (antes era 4 × 7 fijo). */
  enrolledCount?: number | null;
}

export default function TournamentFormatConfigSection({
  token,
  tournamentId,
  divisionId = null,
  initialConfig,
  onChange,
  onSaved,
  submitLabel,
  renderActions,
  inheritedTeamPlay = null,
  enrolledCount = null,
}: Props) {
  const { askConfirm } = useAbortableConfirm([tournamentId, divisionId]);
  const [config, setConfig] = useState<TournamentFormatConfig>(initialConfig);
  const [pendingType, setPendingType] = useState<TournamentType>(
    initialConfig.format_type,
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roundCountDraft, setRoundCountDraft] = useState<string | null>(null);
  const [roundDurationDraft, setRoundDurationDraft] = useState<string | null>(null);
  const [playoffPlayersDraft, setPlayoffPlayersDraft] = useState<string | null>(null);
  const [playoffRoundCountDraft, setPlayoffRoundCountDraft] = useState<string | null>(null);
  const [roundRobinPhaseDrafts, setRoundRobinPhaseDrafts] = useState<Record<string, string>>({});
  const [offsetDrafts, setOffsetDrafts] = useState<Record<number, string>>({});
  const [activeParticipantCount, setActiveParticipantCount] = useState<number | null>(null);
  useEffect(() => {
    setConfig(initialConfig);
    setPendingType(initialConfig.format_type);
  }, [initialConfig]);

  useEffect(() => {
    let cancelled = false;
    async function loadParticipantCount() {
      try {
        const response = await api.participants.list(token, tournamentId, divisionId ? { division_id: divisionId } : undefined);
        if (!cancelled) {
          setActiveParticipantCount(
            response.data.participants.filter((p) => p.current_status !== "dropped").length,
          );
        }
      } catch {
        if (!cancelled) setActiveParticipantCount(null);
      }
    }
    void loadParticipantCount();
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, divisionId]);

  // Órdenes de desempate personalizados ya usados en OTROS torneos del admin,
  // para reutilizarlos con un clic. Si falla, el select queda como siempre.
  const [tiebreakPresets, setTiebreakPresets] = useState<TiebreakPreset[]>([]);
  useEffect(() => {
    let cancelled = false;
    // Promise.resolve: un mock de api sin este método no rompe el render.
    Promise.resolve()
      .then(() => api.tournaments.listTiebreakPresets(token, tournamentId))
      .then((response) => {
        if (!cancelled) setTiebreakPresets(response.data.presets);
      })
      .catch(() => {
        if (!cancelled) setTiebreakPresets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId]);

  const ladder: LadderFormatConfig = useMemo(() => {
    if (isLadderConfig(config.format_type, config.config_json)) {
      const cfg = config.config_json;
      const roundCount = Math.max(1, cfg.round_count || 1);
      const playoffsEnabled = cfg.playoffs_enabled ?? ((cfg.playoff_round_count ?? 0) > 0);
      const playoffRoundCount = playoffsEnabled ? Math.max(1, cfg.playoff_round_count ?? 3) : 0;
      const playoffPlayersCount = playoffsEnabled ? Math.max(2, cfg.playoff_players_count ?? 8) : 0;
      return {
        ...cfg,
        match_sets_format: cfg.match_sets_format ?? "best_of_3",
        set_game_type: cfg.set_game_type ?? "normal_6",
        with_advantage: cfg.with_advantage ?? true,
        set_tiebreak_games: cfg.set_tiebreak_games ?? 6,
        third_set_super_tiebreak_points: cfg.third_set_super_tiebreak_points ?? 10,
        playoffs_enabled: playoffsEnabled,
        playoff_players_count: playoffPlayersCount,
        playoff_round_count: playoffRoundCount,
        playoff_sets_format: cfg.playoff_sets_format ?? "best_of_3",
        playoff_rounds: resizePlayoffRounds(cfg.playoff_rounds, playoffRoundCount, roundCount, playoffPlayersCount),
        weekly_patterns: resizeWeeklyPatterns(cfg.weekly_patterns, roundCount),
      };
    }
    const fallbackRoundCount = 8;
    return {
      frequency: "weekly",
      round_count: fallbackRoundCount,
      start_date: new Date().toISOString().slice(0, 10),
      round_duration_days: 7,
      matches_per_player_per_round: 1,
      match_sets_format: "best_of_3",
      set_game_type: "normal_6",
      with_advantage: true,
      set_tiebreak_games: 6,
      third_set_super_tiebreak_points: 10,
      playoffs_enabled: false,
      playoff_players_count: 0,
      playoff_round_count: 0,
      playoff_sets_format: "best_of_3",
      playoff_rounds: [],
      ranking_scope: "group_current_rank",
      matchup_rule: "adjacent_rank",
      allow_repeat_opponents: true,
      dropout_policy_after_publish: "manual_default",
      requires_admin_publish: true,
      weekly_patterns: resizeWeeklyPatterns(undefined, fallbackRoundCount),
    };
  }, [config]);

  const roundRobinDefaults = useMemo(() => roundRobinDefaultsForEnrolled(enrolledCount), [enrolledCount]);
  const roundRobin: RoundRobinFormatConfig = useMemo(() => {
    const fallback: RoundRobinFormatConfig = {
      schema_version: 2,
      format_mode: "round_robin_playoffs",
      group_stage: {
        group_count: roundRobinDefaults.groupCount,
        players_per_group: roundRobinDefaults.playersPerGroup,
        schedule_type: "full_round_robin",
        rounds_per_pair: 1,
        matches_per_player: null,
        ranking_criteria: ["matches_won", "sets_difference", "games_difference", "head_to_head", "manual"],
      },
      qualification: {
        rules: DEFAULT_ROUND_ROBIN_QUALIFICATION_RULES,
        seed_method: "overall_performance",
        manual_override: true,
      },
      playoff: {
        type: "single_elimination",
        bracket_policy: "auto_preliminary",
        manual_seed_edit: true,
        third_place_match: true,
        sets_format: "best_of_3",
        set_game_type: "normal_6",
        with_advantage: true,
        set_tiebreak_games: 6,
        third_set_super_tiebreak_points: 10,
      },
      rounds_per_pair: 1,
      frequency: "weekly",
      round_count: 8,
      start_date: new Date().toISOString().slice(0, 10),
      round_duration_days: 7,
      match_sets_format: "best_of_3",
      set_game_type: "normal_6",
      with_advantage: true,
      set_tiebreak_games: 6,
      third_set_super_tiebreak_points: 10,
      playoff_players_count: 8,
      playoff_round_count: 3,
      playoff_sets_format: "best_of_3",
      ranking_scope: "group_current_rank",
      notes: null,
      // Preset ITF de Eduardo por default SOLO para config nueva (sin
      // config previa persistida). Ver merge abajo para configs existentes.
      standings_rules: itfStandingsRules(),
      team_play: inheritedTeamPlay,
    };
    if (isRoundRobinConfig(config.format_type, config.config_json)) {
      const cfg = config.config_json as Partial<RoundRobinFormatConfig>;
      return {
        ...fallback,
        ...cfg,
        schema_version: cfg.schema_version === 2 ? 2 : fallback.schema_version,
        format_mode: cfg.format_mode ?? fallback.format_mode,
        group_stage: {
          ...fallback.group_stage,
          ...(cfg.group_stage ?? {}),
          group_count: Math.max(1, Number(cfg.group_stage?.group_count) || fallback.group_stage!.group_count),
          players_per_group: Math.max(1, Number(cfg.group_stage?.players_per_group) || fallback.group_stage!.players_per_group),
          schedule_type: cfg.group_stage?.schedule_type ?? fallback.group_stage!.schedule_type,
          rounds_per_pair: cfg.group_stage?.rounds_per_pair === 2 ? 2 : 1,
          matches_per_player:
            cfg.group_stage?.matches_per_player !== undefined && cfg.group_stage?.matches_per_player !== null
              ? Math.max(1, Number(cfg.group_stage.matches_per_player) || fallback.round_count)
              : null,
          ranking_criteria: cfg.group_stage?.ranking_criteria?.length
            ? cfg.group_stage.ranking_criteria
            : fallback.group_stage!.ranking_criteria,
        },
        qualification: {
          ...fallback.qualification!,
          ...(cfg.qualification ?? {}),
          rules: cfg.qualification?.rules?.length ? cfg.qualification.rules : fallback.qualification!.rules,
          seed_method: cfg.qualification?.seed_method ?? fallback.qualification!.seed_method,
          cross_group_criterion: cfg.qualification?.cross_group_criterion ?? "standings",
          manual_override: cfg.qualification?.manual_override ?? fallback.qualification!.manual_override,
        },
        playoff: {
          ...fallback.playoff!,
          ...(cfg.playoff ?? {}),
          type: "single_elimination",
          bracket_policy: cfg.playoff?.bracket_policy ?? fallback.playoff!.bracket_policy,
          manual_seed_edit: cfg.playoff?.manual_seed_edit ?? fallback.playoff!.manual_seed_edit,
          // Sin la clave (configs viejas) el backend juega el 3er lugar: igual aquí.
          third_place_match: cfg.playoff?.third_place_match !== false,
          avoid_same_group_first_round: cfg.playoff?.avoid_same_group_first_round !== false,
          sets_format: cfg.playoff?.sets_format ?? cfg.playoff_sets_format ?? fallback.playoff!.sets_format,
          set_game_type: cfg.playoff?.set_game_type ?? cfg.set_game_type ?? fallback.set_game_type,
          with_advantage: cfg.playoff?.with_advantage ?? cfg.with_advantage ?? fallback.with_advantage,
          set_tiebreak_games: cfg.playoff?.set_tiebreak_games ?? cfg.set_tiebreak_games ?? fallback.set_tiebreak_games,
          third_set_super_tiebreak_points: cfg.playoff?.third_set_super_tiebreak_points ?? cfg.third_set_super_tiebreak_points ?? fallback.third_set_super_tiebreak_points,
        },
        rounds_per_pair: cfg.rounds_per_pair === 2 ? 2 : 1,
        frequency: cfg.frequency ?? fallback.frequency,
        round_count: Math.max(1, Number(cfg.round_count) || fallback.round_count),
        start_date: cfg.start_date || fallback.start_date,
        round_duration_days:
          cfg.round_duration_days !== undefined &&
          Number.isFinite(Number(cfg.round_duration_days))
            ? Math.max(0, Number(cfg.round_duration_days))
            : fallback.round_duration_days,
        match_sets_format: cfg.match_sets_format ?? fallback.match_sets_format,
        set_game_type: cfg.set_game_type ?? fallback.set_game_type,
        with_advantage: cfg.with_advantage ?? fallback.with_advantage,
        set_tiebreak_games: cfg.set_tiebreak_games ?? fallback.set_tiebreak_games,
        third_set_super_tiebreak_points: cfg.third_set_super_tiebreak_points ?? fallback.third_set_super_tiebreak_points,
        playoff_players_count:
          cfg.playoff_players_count !== undefined &&
          Number.isFinite(Number(cfg.playoff_players_count))
            ? Math.max(0, Number(cfg.playoff_players_count))
            : fallback.playoff_players_count,
        playoff_round_count:
          cfg.playoff_round_count !== undefined &&
          Number.isFinite(Number(cfg.playoff_round_count))
            ? Math.max(0, Number(cfg.playoff_round_count))
            : fallback.playoff_round_count,
        playoff_sets_format: cfg.playoff_sets_format ?? fallback.playoff_sets_format,
        ranking_scope: cfg.ranking_scope ?? fallback.ranking_scope,
        notes: cfg.notes ?? null,
        // Eduardo 2026-09-16: "Sistema anterior" ya no se ofrece — confunde a
        // un admin nuevo. Una config sin standings_rules se presenta como ITF;
        // el backend sigue en modo anterior hasta que el admin GUARDE el
        // Paso 5 (ahí adopta ITF; puede reordenar tablas de torneos viejos).
        standings_rules: cfg.standings_rules ?? itfStandingsRules(),
        team_play: cfg.team_play ?? inheritedTeamPlay,
      };
    }
    return fallback;
  }, [config, inheritedTeamPlay, roundRobinDefaults]);

  const elimination: EliminationFormatConfig = useMemo(() => {
    // Default del bracket = la talla que más le queda a los inscritos
    // (pedido Eduardo 2026-07-28: con 12 inscritos abría en Ronda 128).
    // Solo aplica cuando NO hay talla guardada; una config guardada manda.
    const suggestedSize = suggestedBracketSize(activeParticipantCount);
    const fallback: EliminationFormatConfig = {
      bracket_size: suggestedSize,
      bracket_round_count: eliminationRoundCount(suggestedSize),
      first_round_bye_count: null,
      seeding_source: "initial_rank",
      default_generation_mode: "ranking",
      match_sets_format: "best_of_3",
      set_game_type: "normal_6",
      with_advantage: true,
      set_tiebreak_games: 6,
      third_set_super_tiebreak_points: 10,
      consolation_bracket: false,
      third_place_match: true,
      notes: null,
      team_play: inheritedTeamPlay,
    };
    if (isEliminationConfig(config.format_type, config.config_json)) {
      const cfg = config.config_json as Partial<EliminationFormatConfig>;
      const bracketSize = ELIMINATION_BRACKET_SIZES.includes(cfg.bracket_size as typeof ELIMINATION_BRACKET_SIZES[number])
        ? Number(cfg.bracket_size)
        : fallback.bracket_size;
      return {
        ...fallback,
        ...cfg,
        bracket_size: bracketSize,
        bracket_round_count: eliminationRoundCount(bracketSize),
        first_round_bye_count:
          cfg.first_round_bye_count !== undefined &&
          cfg.first_round_bye_count !== null &&
          Number.isFinite(Number(cfg.first_round_bye_count))
            ? Math.max(0, Math.min(Number(cfg.first_round_bye_count), bracketSize))
            : null,
        default_generation_mode:
          cfg.default_generation_mode === "random" ? "random" : "ranking",
        match_sets_format: cfg.match_sets_format ?? fallback.match_sets_format,
        set_game_type: cfg.set_game_type ?? fallback.set_game_type,
        with_advantage: cfg.with_advantage ?? fallback.with_advantage,
        set_tiebreak_games: cfg.set_tiebreak_games ?? fallback.set_tiebreak_games,
        third_set_super_tiebreak_points: cfg.third_set_super_tiebreak_points ?? fallback.third_set_super_tiebreak_points,
        third_place_match: cfg.third_place_match !== false,
        notes: cfg.notes ?? null,
        team_play: cfg.team_play ?? inheritedTeamPlay,
      };
    }
    return fallback;
  }, [config, activeParticipantCount, inheritedTeamPlay]);

  // Categorías por equipos: los grupos se llenan de equipos, no de jugadores.
  const rrUnit = roundRobin.team_play?.enabled ? "equipo" : "jugador";
  const elimUnit = elimination.team_play?.enabled ? "equipo(s)" : "jugador(es)";
  const isDirtyType = pendingType !== config.format_type;
  const eliminationPlan = eliminationPlanForBracket(
    activeParticipantCount,
    elimination.bracket_size,
    elimination.first_round_bye_count,
  );
  const eliminationPreview = useMemo(
    () => eliminationFormatPreview(
      elimination.bracket_size,
      activeParticipantCount,
      elimination.first_round_bye_count,
    ),
    [elimination.bracket_size, activeParticipantCount, elimination.first_round_bye_count],
  );
  // Gate #149 (2ª ronda): el summary lee los MISMOS números del preview —
  // una sola fuente de verdad. En Ronda 16 parcial (semántica de producción)
  // NO hay Preliminar sino descansos dentro de la plantilla;
  // eliminationPlanForBracket solo conoce la semántica histórica de previa
  // colapsada y contradecía al preview. El caso de bracket sobredimensionado
  // (suggestion warning) conserva el texto genérico de siempre — ahí manda
  // la advertencia del preview, sin estimar BYEs que la generación ajusta.
  const summaryPlayIn = eliminationPreview?.playInMatches ?? eliminationPlan.preliminaryMatches;
  const summaryResting = eliminationPreview?.restingPlayers ?? eliminationPlan.directAdvancers;
  const summaryByesWithinBracket =
    summaryPlayIn === 0
    && summaryResting > 0
    && eliminationPreview !== null
    && (eliminationPreview.suggestion === null || eliminationPreview.suggestion.tone === "info");
  async function persist(
    nextType: TournamentType,
    nextJson: Record<string, unknown>,
    successMsg: string,
  ): Promise<TournamentFormatConfig | null> {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      let nextConfig: TournamentFormatConfig;
      if (divisionId) {
        const response = await api.tournaments.updateDivision(
          token,
          tournamentId,
          divisionId,
          { format_type: nextType, format_config_json: nextJson },
        );
        nextConfig = {
          tournament_id: tournamentId,
          format_type: response.data.division.format_type ?? nextType,
          config_json: (response.data.division.format_config_json ?? nextJson) as TournamentFormatConfigJson,
          created_at: response.data.division.created_at ?? null,
          updated_at: response.data.division.updated_at ?? null,
        };
      } else {
        const response = await api.tournaments.updateFormatConfig(
          token,
          tournamentId,
          { format_type: nextType, config_json: nextJson },
        );
        nextConfig = response.data.format_config;
      }
      setConfig(nextConfig);
      setPendingType(nextConfig.format_type);
      setMessage(successMsg);
      onChange?.(nextConfig);
      return nextConfig;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el formato.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleSelect(nextType: TournamentType) {
    if (saving) return;
    if (nextType === config.format_type) {
      setPendingType(nextType);
      return;
    }
    // Candado de formato (Eduardo 2026-08-27): antes se persistía al primer
    // click sin aviso — un click distraído a media competencia cambiaba el
    // formato. El backend además rechaza el cambio si ya hay resultados,
    // calendario o jornadas publicadas (y con draw virgen lo cancela limpio).
    if (
      config.format_type &&
      !(await askConfirm(
        `¿Cambiar el formato de ${labelFor(config.format_type)} a ${labelFor(nextType)}? ` +
          "Si ya hay draw sin jugar, sus partidos se cancelarán para generar el nuevo formato; " +
          "si ya hay resultados o calendario, el cambio será rechazado.",
        { acceptLabel: `Sí, cambiar a ${labelFor(nextType)}`, tone: "danger" },
      ))
    ) {
      return;
    }
    setPendingType(nextType);
    // Switching to a different type: persist with default config.
    const ok = await persist(
      nextType,
      {},
      `Formato cambiado a ${labelFor(nextType)}.`,
    );
    if (!ok) {
      // Rechazado por el candado del backend: la tarjeta regresa al formato
      // real para no mostrar seleccionado un tipo que no se guardó.
      setPendingType(config.format_type);
    }
  }

  function ladderWithCommittedDrafts(): LadderFormatConfig {
    let next: LadderFormatConfig = { ...ladder };
    const commitNumber = (draft: string | null, current: number, minimum: number) => {
      if (draft === null) return current;
      const parsed = Number(draft);
      return Number.isFinite(parsed) && parsed >= minimum ? parsed : current;
    };
    const nextRoundCount = commitNumber(roundCountDraft, ladder.round_count, 1);
    const nextPlayoffPlayersCount = ladder.playoffs_enabled
      ? commitNumber(playoffPlayersDraft, ladder.playoff_players_count, 2)
      : 0;
    const nextPlayoffRoundCount = ladder.playoffs_enabled
      ? commitNumber(playoffRoundCountDraft, ladder.playoff_round_count, 1)
      : 0;
    next = {
      ...next,
      round_count: nextRoundCount,
      playoff_players_count: nextPlayoffPlayersCount,
      playoff_round_count: nextPlayoffRoundCount,
      weekly_patterns: resizeWeeklyPatterns(ladder.weekly_patterns, nextRoundCount),
      playoff_rounds: resizePlayoffRounds(
        ladder.playoff_rounds,
        nextPlayoffRoundCount,
        nextRoundCount,
        nextPlayoffPlayersCount,
      ),
    };
    return next;
  }

  async function handleLadderSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextLadder = ladderWithCommittedDrafts();
    const ladderScoringError = nextLadder.special_scoring ? specialScoringError(normalizeSpecialScoring(nextLadder.special_scoring)) : null;
    if (ladderScoringError) {
      setMessage(null);
      setError(ladderScoringError);
      return;
    }
    setRoundCountDraft(null);
    setPlayoffPlayersDraft(null);
    setPlayoffRoundCountDraft(null);
    const saved = await persist(
      "ladder",
      { ...nextLadder },
      "Configuración de Escalera guardada.",
    );
    if (saved) onSaved?.(saved);
  }

  // Regla de la liga por puntos validada EN EL FRONTEND antes de enviar
  // (win > loss_completed >= walkover_loss), con el mismo texto que el 409
  // del backend. El backend sigue siendo la autoridad al guardar.
  const standingsPointsError =
    standingsRulesKind(roundRobin.standings_rules) === "points"
      ? standingsPointsRelationError(roundRobin.standings_rules?.points)
      : null;

  async function handleRoundRobinSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (standingsPointsError) {
      setMessage(null);
      setError(standingsPointsError);
      return;
    }
    const teamPlayError = teamPlayValidationError(roundRobin.team_play);
    if (teamPlayError) {
      setMessage(null);
      setError(teamPlayError);
      return;
    }
    const scoringError = roundRobin.special_scoring ? specialScoringError(normalizeSpecialScoring(roundRobin.special_scoring)) : null;
    if (scoringError) {
      setMessage(null);
      setError(scoringError);
      return;
    }
    const saved = await persist(
      "round_robin",
      { ...roundRobin },
      "Configuración de Round Robin guardada.",
    );
    if (saved) onSaved?.(saved);
  }

  async function handleEliminationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const teamPlayError = teamPlayValidationError(elimination.team_play);
    if (teamPlayError) {
      setMessage(null);
      setError(teamPlayError);
      return;
    }
    // Candado suave: guardar un tamaño incoherente con los inscritos (Ronda
    // 128 para 18 jugadores) exige confirmación explícita. Así nació el
    // desmadre de la categoría B del Mex Tour Cup.
    if (eliminationPreview?.suggestion?.tone === "warning") {
      const proceed = await askConfirm(
        `${eliminationPreview.suggestion.text}\n\n¿Guardar Ronda ${elimination.bracket_size} de todas formas?`,
        { acceptLabel: "Sí, guardar de todas formas" },
      );
      if (!proceed) return;
    }
    const saved = await persist(
      "elimination",
      { ...elimination },
      "Configuración de Eliminatoria guardada.",
    );
    if (saved) onSaved?.(saved);
  }

  function updateLadder<K extends keyof LadderFormatConfig>(
    key: K,
    value: LadderFormatConfig[K],
  ) {
    const next: LadderFormatConfig = { ...ladder, [key]: value };
    if (key === "frequency" && value !== "custom") {
      next.round_duration_days =
        FREQUENCY_DURATIONS[value as LadderFrequency] ?? 7;
    }
    if (key === "round_count") {
      const safeCount = Math.max(1, Number(value) || 1);
      next.round_count = safeCount;
      next.weekly_patterns = resizeWeeklyPatterns(
        ladder.weekly_patterns,
        safeCount,
      );
      next.playoff_rounds = resizePlayoffRounds(
        ladder.playoff_rounds,
        next.playoff_round_count,
        safeCount,
        next.playoff_players_count,
      );
    }
    if (key === "playoffs_enabled") {
      const enabled = Boolean(value);
      next.playoffs_enabled = enabled;
      if (!enabled) {
        next.playoff_players_count = 0;
        next.playoff_round_count = 0;
        next.playoff_rounds = [];
      } else {
        next.playoff_players_count = Math.max(2, ladder.playoff_players_count || 6);
        next.playoff_round_count = Math.max(1, ladder.playoff_round_count || 3);
        next.playoff_rounds = resizePlayoffRounds(
          ladder.playoff_rounds,
          next.playoff_round_count,
          next.round_count,
          next.playoff_players_count,
        );
      }
    }
    if (key === "playoff_players_count") {
      next.playoff_players_count = next.playoffs_enabled ? Math.max(2, Number(value) || 2) : 0;
      next.playoff_rounds = resizePlayoffRounds(
        ladder.playoff_rounds,
        next.playoff_round_count,
        next.round_count,
        next.playoff_players_count,
      );
    }
    if (key === "playoff_round_count") {
      next.playoff_round_count = next.playoffs_enabled ? Math.max(1, Number(value) || 1) : 0;
      next.playoff_rounds = resizePlayoffRounds(
        ladder.playoff_rounds,
        next.playoff_round_count,
        next.round_count,
        next.playoff_players_count,
      );
    }
    setConfig({ ...config, format_type: "ladder", config_json: next });
  }

  function updateWeeklyPattern(
    week: number,
    patch: Partial<LadderWeeklyPatternEntry>,
  ) {
    const next = ladder.weekly_patterns.map((entry) => {
      if (entry.week !== week) return entry;
      const merged: LadderWeeklyPatternEntry = { ...entry, ...patch };
      if (patch.pattern !== undefined) {
        if (isOffsetPattern(patch.pattern)) {
          merged.offset = entry.offset ?? DEFAULT_OFFSET;
        } else {
          merged.offset = null;
        }
      }
      return merged;
    });
    setConfig({
      ...config,
      format_type: "ladder",
      config_json: { ...ladder, weekly_patterns: next },
    });
  }

  function updatePlayoffRound(roundIndex: number, patch: Partial<LadderPlayoffRoundEntry>) {
    const next = ladder.playoff_rounds.map((entry) =>
      entry.round_index === roundIndex ? { ...entry, ...patch } : entry,
    );
    setConfig({
      ...config,
      format_type: "ladder",
      config_json: { ...ladder, playoff_rounds: next },
    });
  }

  function applyOfficial14WeekPattern() {
    if (ladder.round_count !== OFFICIAL_PATTERN_WEEKS) return;
    setConfig({
      ...config,
      format_type: "ladder",
      config_json: {
        ...ladder,
        weekly_patterns: buildOfficial14WeekPattern(),
      },
    });
  }

  function updateRoundRobin<K extends keyof RoundRobinFormatConfig>(
    key: K,
    value: RoundRobinFormatConfig[K],
  ) {
    const next: RoundRobinFormatConfig = { ...roundRobin, [key]: value };
    if (key === "frequency" && value !== "custom") {
      next.round_duration_days =
        FREQUENCY_DURATIONS[value as LadderFrequency] ?? 7;
    }
    if (key === "round_count") {
      next.round_count = Math.max(1, Number(value) || 1);
    }
    setConfig({
      ...config,
      format_type: "round_robin",
      config_json: next,
    });
  }

  function updateRoundRobinPhase(patch: Partial<RoundRobinFormatConfig>) {
    const next: RoundRobinFormatConfig = { ...roundRobin, ...patch };
    // "Todos contra todos": el # de jornadas se deriva de jugadores por grupo y
    // vueltas. El input está deshabilitado en este modo, así que sincronizamos
    // round_count en cada cambio — antes se quedaba el valor viejo (p.ej. 8
    // jornadas con 2 jugadores por grupo).
    const stage = next.group_stage;
    if (stage?.schedule_type === "full_round_robin" && (stage.players_per_group ?? 0) > 1) {
      const players = stage.players_per_group ?? 0;
      const base = players % 2 === 0 ? players - 1 : players;
      next.round_count = base * (stage.rounds_per_pair === 2 ? 2 : 1);
    }
    setConfig({
      ...config,
      format_type: "round_robin",
      config_json: next,
    });
  }

  function updateRoundRobinGroupStage(patch: Partial<NonNullable<RoundRobinFormatConfig["group_stage"]>>) {
    updateRoundRobinPhase({
      group_stage: { ...roundRobin.group_stage!, ...patch },
    });
  }

  function updateRoundRobinQualificationRule(index: number, patch: Partial<RoundRobinQualificationRule>) {
    const rules = [...(roundRobin.qualification?.rules ?? DEFAULT_ROUND_ROBIN_QUALIFICATION_RULES)];
    rules[index] = { ...rules[index], ...patch } as RoundRobinQualificationRule;
    updateRoundRobinPhase({
      qualification: { ...roundRobin.qualification!, rules },
    });
  }

  function updateRoundRobinPlayoff(patch: Partial<NonNullable<RoundRobinFormatConfig["playoff"]>>) {
    updateRoundRobinPhase({
      playoff: { ...roundRobin.playoff!, ...patch },
    });
  }

  function setRoundRobinPhaseDraft(key: string, value: string) {
    setRoundRobinPhaseDrafts((current) => ({ ...current, [key]: value }));
  }

  function commitRoundRobinPhaseNumber(
    key: string,
    currentValue: number,
    minimum: number,
    apply: (value: number) => void,
  ) {
    const draft = roundRobinPhaseDrafts[key];
    if (draft === undefined) return;
    setRoundRobinPhaseDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    const parsed = Number(draft);
    const safe = Number.isFinite(parsed) && parsed >= minimum ? parsed : currentValue;
    if (safe !== currentValue) apply(safe);
  }

  function commitRoundRobinPhaseOptionalNumber(
    key: string,
    currentValue: number | null,
    minimum: number,
    apply: (value: number | null) => void,
  ) {
    const draft = roundRobinPhaseDrafts[key];
    if (draft === undefined) return;
    setRoundRobinPhaseDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    if (draft.trim() === "") {
      if (currentValue !== null) apply(null);
      return;
    }
    const parsed = Number(draft);
    const safe = Number.isFinite(parsed) && parsed >= minimum ? parsed : currentValue;
    if (safe !== currentValue) apply(safe);
  }

  function updateElimination<K extends keyof EliminationFormatConfig>(
    key: K,
    value: EliminationFormatConfig[K],
  ) {
    const next: EliminationFormatConfig = { ...elimination, [key]: value };
    if (key === "bracket_size") {
      const bracketSize = Number(value);
      next.bracket_round_count = eliminationRoundCount(bracketSize);
      if (next.first_round_bye_count !== null) {
        next.first_round_bye_count = Math.min(next.first_round_bye_count, bracketSize);
      }
    }
    if (key === "first_round_bye_count" && value !== null) {
      next.first_round_bye_count = Math.max(0, Math.min(Number(value) || 0, next.bracket_size));
    }
    setConfig({
      ...config,
      format_type: "elimination",
      config_json: next,
    });
  }

  const topGroupRule = roundRobin.qualification?.rules.find((rule) => rule.type === "top_n_per_group");
  const wildcardRule = roundRobin.qualification?.rules.find((rule) => rule.type === "best_n_by_group_position");
  const directPositionsLabel = topGroupRule?.type === "top_n_per_group"
    ? `${topGroupRule.positions_from}–${topGroupRule.positions_to}`
    : "1–2";
  // Feedback de Eduardo (2026-07-14): 0 wildcards es válido — la posición se
  // va sola a 0 y se bloquea; al reactivar cantidad, la posición regresa a 3.
  const wildcardCount = wildcardRule?.type === "best_n_by_group_position" ? wildcardRule.count : 1;
  const wildcardLabel = wildcardRule?.type === "best_n_by_group_position"
    ? (wildcardRule.count === 0 ? "sin wildcards" : `mejor(es) ${wildcardRule.count} del lugar ${wildcardRule.group_position}`)
    : "mejor 3°";
  const draftNumber = (draft: string | undefined, fallback: number) => {
    if (draft === undefined || draft.trim() === "") return fallback;
    const parsed = Number(draft);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const savedRule0 = roundRobin.qualification?.rules[0];
  const savedRule1 = roundRobin.qualification?.rules[1];
  const qualificationSummaryCount = draftNumber(roundRobinPhaseDrafts.wildcard_count, wildcardCount);
  const groupScheduleType = roundRobin.group_stage?.schedule_type ?? "full_round_robin";
  const groupScheduleLabel = ROUND_ROBIN_GROUP_SCHEDULE_LABELS[groupScheduleType];
  const playersPerGroup = roundRobin.group_stage?.players_per_group ?? 0;
  const minimumFullRoundRobinJornadas = playersPerGroup > 1
    ? ((playersPerGroup % 2 === 0 ? playersPerGroup - 1 : playersPerGroup) * (roundRobin.group_stage?.rounds_per_pair === 2 ? 2 : 1))
    : 0;
  const groupScheduleDescription = groupScheduleType === "full_round_robin"
    ? `Todos juegan contra todos; con ${playersPerGroup || "—"} ${rrUnit}es requiere ${minimumFullRoundRobinJornadas || "—"} jornada(s) reales.`
    : groupScheduleType === "limited_schedule"
      ? `Cada jugador juega ${roundRobin.group_stage?.matches_per_player ?? roundRobin.round_count} partido(s); una jornada no repite jugador.`
      : "El admin captura los cruces manualmente; el generador automático no crea partidos.";
  const roundRobinPreview = useMemo(
    () => roundRobinFormatPreview({
      groupCount: roundRobin.group_stage?.group_count ?? 1,
      playersPerGroup: roundRobin.group_stage?.players_per_group ?? 0,
      roundsPerPair: roundRobin.group_stage?.rounds_per_pair === 2 ? 2 : 1,
      totalPlayers: activeParticipantCount,
      playoffQualifiers: roundRobin.format_mode === "round_robin_playoffs"
        ? roundRobin.playoff_players_count
        : null,
    }),
    [
      roundRobin.group_stage?.group_count,
      roundRobin.group_stage?.players_per_group,
      roundRobin.group_stage?.rounds_per_pair,
      roundRobin.format_mode,
      roundRobin.playoff_players_count,
      activeParticipantCount,
    ],
  );
  const qualificationSummary = playoffQualificationSummary({
    groupCount: (roundRobinPreview?.groups.length ?? 0) || (roundRobin.group_stage?.group_count ?? 1),
    playersPerGroup: (roundRobinPreview?.groups ?? []).reduce((max, group) => Math.max(max, group.size), 0)
      || (roundRobin.group_stage?.players_per_group ?? 0),
    positionsFrom: draftNumber(roundRobinPhaseDrafts.positions_from, savedRule0?.type === "top_n_per_group" ? savedRule0.positions_from : 1),
    positionsTo: draftNumber(roundRobinPhaseDrafts.positions_to, savedRule0?.type === "top_n_per_group" ? savedRule0.positions_to : 2),
    wildcardPosition: qualificationSummaryCount === 0
      ? 0
      : draftNumber(roundRobinPhaseDrafts.group_position, savedRule1?.type === "best_n_by_group_position" && savedRule1.group_position > 0 ? savedRule1.group_position : 3),
    wildcardCount: qualificationSummaryCount,
    bracketPolicy: roundRobin.playoff?.bracket_policy ?? "auto_preliminary",
    unit: rrUnit === "equipo" ? { singular: "equipo", plural: "equipos" } : { singular: "jugador", plural: "jugadores" },
  });

  return (
    <section className="fmt-step">
      <div className="fmt-cols">
      <div className="fmt-main">
      <section className="card fmt-card fmt-card--type" aria-label="Tipo de torneo">
        <h2 className="fmt-card__title">Tipo de torneo</h2>
        <div className="format-grid">
          {FORMAT_OPTIONS.map((opt) => {
            const selected = pendingType === opt.value;
            const cardLocked = saving && !selected;
            return (
              // div role=button (no <button>): el "?" de la descripción vive
              // adentro y un botón anidado en otro botón es HTML inválido.
              <div
                key={opt.value}
                role="button"
                tabIndex={cardLocked ? -1 : 0}
                className={`format-card ${selected ? "is-selected" : ""}${cardLocked ? " is-disabled" : ""}`}
                onClick={() => { if (!cardLocked) void handleSelect(opt.value); }}
                onKeyDown={(event) => {
                  // Solo teclas sobre la tarjeta misma: un Enter en el "?" de la
                  // descripción no debe cambiar el formato.
                  if (event.target !== event.currentTarget) return;
                  if ((event.key === "Enter" || event.key === " ") && !cardLocked) {
                    event.preventDefault();
                    void handleSelect(opt.value);
                  }
                }}
                aria-pressed={selected}
                aria-disabled={cardLocked}
              >
                {selected && <span className="format-card__check">✓</span>}
                <div className="format-card__illustration">{opt.illustration}</div>
                <h3 className="format-card__title">{opt.title}</h3>
                <span className="format-card__badge">{opt.badge}</span>
                {/* Cowork 2026-09-20: el "?" en medio del título robaba los clics
                    de la tarjeta; ahora vive en la esquina y el resto es un solo botón. */}
                <span className="format-card__hint">
                  <FieldHint label={`¿Cómo funciona ${opt.title}?`}>{opt.description}</FieldHint>
                </span>
              </div>
            );
          })}
        </div>
        {pendingType === "round_robin" ? (
          <div className="fmt-path" role="group" aria-label="Camino de competencia">
            <span className="fmt-path__label hint-label">Camino:<FieldHint>Formato define la arquitectura; Grupos solo asigna jugadores y Armado ejecuta.</FieldHint></span>
            {(["round_robin_only", "round_robin_playoffs"] as RoundRobinFormatMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`fmt-path__pill${(roundRobin.format_mode ?? "round_robin_playoffs") === mode ? " is-active" : ""}`}
                aria-pressed={(roundRobin.format_mode ?? "round_robin_playoffs") === mode}
                onClick={() => updateRoundRobinPhase({ format_mode: mode })}
                disabled={saving}
              >
                {mode === "round_robin_only" ? "Solo grupos / liga" : ROUND_ROBIN_FORMAT_MODE_LABELS[mode]}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {error && (
        <p role="alert" className="form-error" style={{ marginTop: "1rem" }}>
          {error}
        </p>
      )}
      {message && (
        <p className="form-message" style={{ marginTop: "1rem" }}>
          {message}
        </p>
      )}

      {pendingType === "ladder" && (
        <form
          id="format-config-form"
          className="form form--inline format-grid--compact-form"
          onSubmit={handleLadderSubmit}
        >
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Jornadas</h3>
            </div>
            <div className="format-form-section__grid">
              <label>
                Inicio
                <input
                  type="date"
                  value={ladder.start_date}
                  onChange={(e) => updateLadder("start_date", e.target.value)}
                  disabled={saving}
                  required
                />
              </label>
              <label>
                Frecuencia
                <select className="t123-select"
                  value={ladder.frequency}
                  onChange={(e) =>
                    updateLadder("frequency", e.target.value as LadderFrequency)
                  }
                  disabled={saving}
                >
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quincenal</option>
                  <option value="monthly">Mensual</option>
                  <option value="custom">Personalizada</option>
                </select>
              </label>
              <label>
                # de Jornadas
                <input
                  type="number"
                  min={1}
                  value={roundCountDraft ?? String(ladder.round_count)}
                  onChange={(e) => setRoundCountDraft(e.target.value)}
                  onBlur={() => {
                    const draft = roundCountDraft;
                    setRoundCountDraft(null);
                    if (draft === null) return;
                    const parsed = Number(draft);
                    const safe =
                      Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
                    if (safe !== ladder.round_count) {
                      updateLadder("round_count", safe);
                    }
                  }}
                  disabled={saving}
                />
              </label>
              <label>
                Días entre Jornadas
                <input
                  type="number"
                  min={0}
                  value={roundDurationDraft ?? String(ladder.round_duration_days)}
                  onChange={(e) => setRoundDurationDraft(e.target.value)}
                  onBlur={() => {
                    const draft = roundDurationDraft;
                    setRoundDurationDraft(null);
                    if (draft === null) return;
                    const parsed = Number(draft);
                    const safe =
                      Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                    if (safe !== ladder.round_duration_days) {
                      updateLadder("round_duration_days", safe);
                    }
                  }}
                  disabled={saving || ladder.frequency !== "custom"}
                />
              </label>
            </div>
          </div>

          {SHOW_SET_FORMAT_FIELDS ? (
            <div className="format-form-section form__full">
              <div className="format-form-section__header format-form-section__header--split">
                <div>
                  <h3 className="hint-label">Formato Partidos<FieldHint>Define sets normales o minisets infantiles sin afectar la captura libre de resultados.</FieldHint></h3>
                </div>
                <span className="format-scoring-pill">{scoringSummary(ladder)}</span>
              </div>
              <div className="format-form-section__grid format-form-section__grid--scoring">
                <label>
                  # Sets por Partido
                  <select className="t123-select"
                    value={ladder.match_sets_format}
                    onChange={(e) =>
                      updateLadder("match_sets_format", e.target.value as LadderSetFormat)
                    }
                    disabled={saving}
                  >
                    {SET_FORMAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Tipo de Set
                  <select className="t123-select"
                    value={ladder.set_game_type}
                    onChange={(e) => updateLadder("set_game_type", e.target.value as SetGameType)}
                    disabled={saving}
                  >
                    {SET_GAME_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Set Tie Break
                  <select className="t123-select"
                    value={ladder.set_tiebreak_games}
                    onChange={(e) => updateLadder("set_tiebreak_games", Number(e.target.value))}
                    disabled={saving}
                  >
                    {SET_TIEBREAK_GAME_OPTIONS.map((games) => (
                      <option key={games} value={games}>
                        {games} games
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  3er Set
                  <select
                    className="t123-select format-third-set-select"
                    value={ladder.third_set_super_tiebreak_points ?? "none"}
                    onChange={(e) =>
                      updateLadder(
                        "third_set_super_tiebreak_points",
                        e.target.value === "none" ? null : Number(e.target.value),
                      )
                    }
                    disabled={saving}
                  >
                    <option value="none">No aplica</option>
                    {SUPER_TIEBREAK_POINTS_OPTIONS.map((points) => (
                      <option key={points} value={points}>
                        Súper muerte a {points} pts
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form__checkbox format-advantage-toggle">
                  <input
                    type="checkbox"
                    checked={ladder.with_advantage}
                    onChange={(e) => updateLadder("with_advantage", e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    <span className="hint-label">
                      <strong>Con ventaja</strong>
                      <FieldHint>
                        Activado: juegos con ventaja tradicional. Apagado: juegos sin ventaja / punto decisivo.
                      </FieldHint>
                    </span>
                  </span>
                </label>
              </div>
            </div>
          ) : null}

          <div className="format-form-section form__full">
            <div className="format-form-section__header format-form-section__header--split">
              <div>
                <h3>Formato Playoffs</h3>
                <p>Activa playoffs para agregar sus jornadas al calendario competitivo de Escalera.</p>
              </div>
              <label className="form__checkbox format-playoff-toggle">
                <input
                  type="checkbox"
                  checked={ladder.playoffs_enabled}
                  onChange={(e) => updateLadder("playoffs_enabled", e.target.checked)}
                  disabled={saving}
                />
                <span>
                  <span className="hint-label">
                    <strong>Este torneo tendrá playoffs</strong>
                    <FieldHint>
                      Activado, las jornadas playoff aparecen abajo en Sistema de retos; apagado, solo se generan jornadas regulares de Escalera.
                    </FieldHint>
                  </span>
                </span>
              </label>
            </div>
            {ladder.playoffs_enabled && (
              <div className="format-form-section__grid">
                <label>
                  # Jugadores a Playoffs
                  <input
                    type="number"
                    min={2}
                    value={playoffPlayersDraft ?? String(ladder.playoff_players_count)}
                    onChange={(e) => setPlayoffPlayersDraft(e.target.value)}
                    onBlur={() => {
                      const draft = playoffPlayersDraft;
                      setPlayoffPlayersDraft(null);
                      if (draft === null) return;
                      const parsed = Number(draft);
                      const safe = Number.isFinite(parsed) && parsed >= 2 ? parsed : ladder.playoff_players_count;
                      if (safe !== ladder.playoff_players_count) {
                        updateLadder("playoff_players_count", safe);
                      }
                    }}
                    disabled={saving}
                  />
                </label>
                <label>
                  # Jornadas Playoffs
                  <input
                    type="number"
                    min={1}
                    value={playoffRoundCountDraft ?? String(ladder.playoff_round_count)}
                    onChange={(e) => setPlayoffRoundCountDraft(e.target.value)}
                    onBlur={() => {
                      const draft = playoffRoundCountDraft;
                      setPlayoffRoundCountDraft(null);
                      if (draft === null) return;
                      const parsed = Number(draft);
                      const safe = Number.isFinite(parsed) && parsed >= 1 ? parsed : ladder.playoff_round_count;
                      if (safe !== ladder.playoff_round_count) {
                        updateLadder("playoff_round_count", safe);
                      }
                    }}
                    disabled={saving}
                  />
                </label>
                {/* Eduardo 2026-09-21 (torneo 139): se guardó "3 jugadores / 2
                    jornadas" y después los playoffs no se podían generar. El
                    cuadro de Escalera es de 2, 4, 8 o 16 y sus jornadas se
                    deducen de ahí: se avisa aquí mismo, junto a los campos. */}
                {(() => {
                  const players = Number(ladder.playoff_players_count) || 0;
                  const rounds = Number(ladder.playoff_round_count) || 0;
                  if (players < 2) return null;
                  const isPowerOfTwo = (players & (players - 1)) === 0;
                  const expected = isPowerOfTwo ? Math.log2(players) : 0;
                  if (isPowerOfTwo && rounds === expected) return null;
                  return (
                    <p className="field-warning" style={{ gridColumn: "1 / -1", margin: 0 }}>
                      {isPowerOfTwo
                        ? `⚠️ Con ${players} jugadores a playoffs van ${expected} jornada${expected === 1 ? "" : "s"} de playoffs (hoy dice ${rounds}).`
                        : `⚠️ # Jugadores a Playoffs debe ser 2, 4, 8 o 16 (hoy dice ${players}). Con 2 se juega solo la final (1 jornada); con 4, semifinales y final (2 jornadas).`}
                    </p>
                  );
                })()}
                {SHOW_SET_FORMAT_FIELDS ? (
                  <label>
                    # Sets por Partido Playoffs
                    <select className="t123-select"
                      value={ladder.playoff_sets_format}
                      onChange={(e) =>
                        updateLadder("playoff_sets_format", e.target.value as LadderSetFormat)
                      }
                      disabled={saving}
                    >
                      {SET_FORMAT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            )}
          </div>

          {/* Eduardo 2026-09-20: estos dos selects quedaban tan angostos que el
              texto se cortaba ("Ranking actual del g…"); ahora ocupan dos columnas
              y llevan su "?" de ayuda. */}
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Reglas de la Escalera</h3>
            </div>
            <div className="format-form-section__grid">
              <label className="form__span2">
                <span className="hint-label">
                  Ranking utilizado
                  <FieldHint label="Qué es Ranking utilizado">
                    De qué posiciones salen los retos que el sistema sugiere en cada jornada.
                    «Ranking actual del grupo»: las posiciones como van quedando semana a semana (lo normal en una Escalera: quien sube reta desde su nuevo lugar).
                    «Ranking inicial del grupo»: siempre los lugares con los que arrancó el grupo, sin importar los resultados.
                    «Manual»: pensado para armar los retos a mano; la sugerencia sale del ranking actual y tú la editas antes de publicar.
                  </FieldHint>
                </span>
                <select className="t123-select"
                  value={ladder.ranking_scope}
                  onChange={(e) =>
                    updateLadder("ranking_scope", e.target.value as LadderRankingScope)
                  }
                  disabled={saving}
                >
                  <option value="group_current_rank">Ranking actual del grupo</option>
                  <option value="group_initial_rank">Ranking inicial del grupo</option>
                  <option value="manual">Manual</option>
                </select>
              </label>
              <label className="form__span2">
                <span className="hint-label">
                  Política de baja post-publicación
                  <FieldHint label="Qué es la Política de baja">
                    Qué se hace con los retos YA publicados de un jugador que se da de baja a media Escalera.
                    Es la regla que declaras para tu torneo: aparece en el resumen del formato para que todos sepan cómo se resuelve; el ajuste lo haces tú en Draw o Calendario.
                    «Baja con confirmación»: el admin confirma la baja y decide cada partido.
                    «Reacomodar manualmente»: el admin le busca otro rival al que se quedó sin reto.
                    «Dejar BYE»: el rival descansa esa jornada.
                    «Cancelar enfrentamiento»: el reto se cancela y no cuenta.
                  </FieldHint>
                </span>
                <select className="t123-select"
                  value={ladder.dropout_policy_after_publish}
                  onChange={(e) =>
                    updateLadder(
                      "dropout_policy_after_publish",
                      e.target.value as LadderDropoutPolicy,
                    )
                  }
                  disabled={saving}
                >
                  <option value="manual_default">Baja con confirmación</option>
                  <option value="manual_reassign">Reacomodar manualmente</option>
                  <option value="bye">Dejar BYE</option>
                  <option value="cancel_matchup">Cancelar enfrentamiento</option>
                </select>
              </label>
              <label className="form__checkbox">
                <input
                  type="checkbox"
                  checked={ladder.allow_repeat_opponents}
                  onChange={(e) =>
                    updateLadder("allow_repeat_opponents", e.target.checked)
                  }
                  disabled={saving}
                />
                <span className="hint-label">
                  Permitir rivales repetidos entre jornadas
                  <FieldHint label="Qué es Permitir rivales repetidos">
                    Si dos jugadores pueden volver a enfrentarse en otra jornada. En una Escalera es normal que sí (retas a quien tienes arriba, aunque ya hayan jugado). Es una regla declarada de tu torneo: si la desmarcas, revisa los retos sugeridos antes de publicar, porque el sistema no los cambia solo.
                  </FieldHint>
                </span>
              </label>
              <label className="form__checkbox">
                <input
                  type="checkbox"
                  checked={ladder.requires_admin_publish}
                  onChange={(e) =>
                    updateLadder("requires_admin_publish", e.target.checked)
                  }
                  disabled={saving}
                />
                <span className="hint-label">
                  Requiere publicación manual del admin por jornada
                  <FieldHint label="Qué es la publicación manual">
                    Los retos de cada jornada se generan como borrador y los jugadores no los ven hasta que tú los revisas y pulsas «Publicar a Calendario». Así puedes corregir un reto antes de que salga.
                  </FieldHint>
                </span>
              </label>
            </div>
          </div>
          <div className="format-form-section form__full format-form-section--patterns">
            <div className="format-form-section__header">
              <h3>Sistema de retos</h3>
            </div>
            <details className="weekly-patterns form__full" open>
              <summary className="weekly-patterns__summary">
                <span className="weekly-patterns__title">
                  Sistema de retos ({ladder.round_count + ladder.playoff_round_count} jornadas)
                </span>
                <span className="weekly-patterns__subtitle">
                  Las semanas regulares usan reglas de Escalera; las jornadas playoff usan reglas de bracket en la misma tabla.
                </span>
              </summary>
              <div className="weekly-patterns__toolbar">
                <button
                  type="button"
                  className="btn btn--ghost weekly-patterns__cta"
                  onClick={applyOfficial14WeekPattern}
                  disabled={
                    saving || ladder.round_count !== OFFICIAL_PATTERN_WEEKS
                  }
                  title={
                    ladder.round_count === OFFICIAL_PATTERN_WEEKS
                      ? undefined
                      : "Configura 14 jornadas para habilitar el patrón oficial."
                  }
                >
                  Aplicar patrón oficial 14 semanas
                </button>
                {ladder.round_count !== OFFICIAL_PATTERN_WEEKS && (
                  <span className="weekly-patterns__hint">
                    Ajusta # de jornadas a 14 para habilitar el patrón oficial.
                  </span>
                )}
              </div>
              <div className="weekly-patterns__table-wrap">
                <table className="weekly-patterns__table weekly-patterns__table--calendar">
                  <thead>
                    <tr>
                      <th scope="col">Jornada</th>
                      <th scope="col">Fase</th>
                      <th scope="col">Patrón / regla</th>
                      <th scope="col">Lugares arriba</th>
                      <th scope="col">BYE / descanso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ladder.weekly_patterns.map((entry) => {
                      const offsetEnabled = isOffsetPattern(entry.pattern);
                      return (
                        <tr key={`regular-${entry.week}`} className="weekly-patterns__row weekly-patterns__row--regular">
                          <th scope="row" className="weekly-patterns__week-cell">
                            <span>Jornada {entry.week}</span>
                          </th>
                          <td data-label="Fase">
                            <span className="weekly-patterns__phase weekly-patterns__phase--regular">Regular</span>
                          </td>
                          <td data-label="Patrón / regla">
                            <select className="t123-select"
                              value={entry.pattern}
                              onChange={(e) =>
                                updateWeeklyPattern(entry.week, {
                                  pattern: e.target.value as LadderWeeklyPattern,
                                })
                              }
                              disabled={saving}
                              aria-label={`Patrón semana ${entry.week}`}
                            >
                              {(
                                Object.keys(WEEKLY_PATTERN_LABELS) as Array<
                                  LadderWeeklyPattern
                                >
                              ).map((value) => (
                                <option key={value} value={value}>
                                  {WEEKLY_PATTERN_LABELS[value]}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td data-label="Lugares arriba">
                            <input
                              type="number"
                              min={1}
                              value={
                                offsetEnabled
                                  ? offsetDrafts[entry.week] ??
                                    String(entry.offset ?? DEFAULT_OFFSET)
                                  : ""
                              }
                              onChange={(e) =>
                                setOffsetDrafts({
                                  ...offsetDrafts,
                                  [entry.week]: e.target.value,
                                })
                              }
                              onBlur={() => {
                                const draft = offsetDrafts[entry.week];
                                if (draft === undefined) return;
                                const parsed = Number(draft);
                                const fallback =
                                  entry.offset ?? DEFAULT_OFFSET;
                                let safe =
                                  Number.isFinite(parsed) && parsed >= 1
                                    ? parsed
                                    : fallback;
                                // The pattern crosses even<->odd positions, so
                                // the offset must be odd; an even one would
                                // leave the whole week without playable pairs
                                // (the backend rejects it with a 409).
                                if (safe % 2 === 0) {
                                  safe = Math.max(1, safe - 1);
                                  setError(null);
                                  setMessage(
                                    "Los lugares arriba deben ser un número impar " +
                                      "(1, 3, 5…) para que un par pueda retar a un non. " +
                                      `Se ajustó a ${safe}.`,
                                  );
                                }
                                const next = { ...offsetDrafts };
                                delete next[entry.week];
                                setOffsetDrafts(next);
                                if (safe !== entry.offset) {
                                  updateWeeklyPattern(entry.week, {
                                    offset: safe,
                                  });
                                }
                              }}
                              disabled={saving || !offsetEnabled}
                              aria-label={`Lugares arriba semana ${entry.week}`}
                              placeholder={offsetEnabled ? "" : "—"}
                              step={2}
                            />
                          </td>
                          <td className="weekly-patterns__bye" data-label="BYE / descanso">
                            <input
                              type="checkbox"
                              checked={entry.bye}
                              onChange={(e) =>
                                updateWeeklyPattern(entry.week, {
                                  bye: e.target.checked,
                                })
                              }
                              disabled={saving}
                              aria-label={`BYE semana ${entry.week}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {ladder.playoff_rounds.map((entry) => (
                      <tr key={`playoff-${entry.round_index}`} className="weekly-patterns__row weekly-patterns__row--playoff">
                        <th scope="row" className="weekly-patterns__week-cell">
                          <span>Jornada {entry.week}</span>
                          <small>Playoff {entry.round_index}</small>
                        </th>
                        <td data-label="Fase">
                          <span className="weekly-patterns__phase weekly-patterns__phase--playoff">Playoffs</span>
                        </td>
                        <td data-label="Patrón / regla">
                          <select className="t123-select"
                            value={entry.rule}
                            onChange={(e) => updatePlayoffRound(entry.round_index, { rule: e.target.value as LadderPlayoffRoundRule })}
                            disabled={saving}
                            aria-label={`Regla playoff jornada ${entry.week}`}
                          >
                            {PLAYOFF_ROUND_RULE_OPTIONS.map((value) => (
                              <option key={value} value={value}>
                                {PLAYOFF_ROUND_RULE_LABELS[value]}
                              </option>
                            ))}
                          </select>
                          <small className="weekly-patterns__rule-helper">{playoffRoundHelper(entry.rule)}</small>
                        </td>
                        <td data-label="Lugares arriba">—</td>
                        <td className="weekly-patterns__bye weekly-patterns__bye--text" data-label="BYE / descanso">
                          {entry.rule === "top_6_preliminary" ? "1º y 2º directo" : "Según ganadores"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
          <SpecialScoringSection
            value={ladder.special_scoring}
            onChange={(next) => updateLadder("special_scoring", next)}
            scope="exceptions"
            disabled={saving}
          />
        </form>
      )}

      {pendingType === "round_robin" && (
        <form
          id="format-config-form"
          className="form form--inline format-grid--compact-form"
          onSubmit={handleRoundRobinSubmit}
        >
          {/* Eduardo 2026-09-15: "Por equipos" justo debajo de las tarjetas de formato. */}
          <TeamPlayFormatSection
            value={roundRobin.team_play}
            disabled={saving}
            onChange={(next) => updateRoundRobin("team_play", next)}
          />
          <div className="format-round-count-hint form__full" role="note">
            {groupScheduleType === "full_round_robin"
              ? `Se calcula automático: ${minimumFullRoundRobinJornadas || "—"} jornada(s) para todos contra todos.`
              : "En calendario limitado, una jornada equivale a máximo 1 partido por jugador."}
          </div>
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Grupos y jornadas</h3>
            </div>
            <div className="format-form-section__grid">
              {typeof enrolledCount === "number" && enrolledCount >= 2 ? (
                <p className="muted" style={{ gridColumn: "1 / -1", margin: 0 }}>
                  Esta categoría tiene <strong>{enrolledCount}</strong> inscrito{enrolledCount === 1 ? "" : "s"} hoy.
                  {(roundRobin.group_stage?.group_count ?? 1) * (roundRobin.group_stage?.players_per_group ?? 0) < enrolledCount
                    ? " Con estos grupos no caben todos: sube el número de grupos o de jugadores por grupo."
                    : ""}
                </p>
              ) : null}
              <label>
                Número de grupos
                <input
                  type="number"
                  min={1}
                  value={roundRobinPhaseDrafts.group_count ?? String(roundRobin.group_stage?.group_count ?? 4)}
                  onChange={(e) => setRoundRobinPhaseDraft("group_count", e.target.value)}
                  onBlur={() => commitRoundRobinPhaseNumber(
                    "group_count",
                    roundRobin.group_stage?.group_count ?? 4,
                    1,
                    (value) => updateRoundRobinGroupStage({ group_count: value }),
                  )}
                  disabled={saving}
                />
              </label>
              <label>
                {rrUnit === "equipo" ? "Equipos por grupo" : "Jugadores por grupo"}
                <input
                  type="number"
                  min={1}
                  value={roundRobinPhaseDrafts.players_per_group ?? String(roundRobin.group_stage?.players_per_group ?? 7)}
                  onChange={(e) => setRoundRobinPhaseDraft("players_per_group", e.target.value)}
                  onBlur={() => commitRoundRobinPhaseNumber(
                    "players_per_group",
                    roundRobin.group_stage?.players_per_group ?? 7,
                    1,
                    (value) => updateRoundRobinGroupStage({ players_per_group: value }),
                  )}
                  disabled={saving}
                />
              </label>
              <label>
                Calendario de grupos
                <select className="t123-select"
                  value={roundRobin.group_stage?.schedule_type ?? "full_round_robin"}
                  onChange={(e) => {
                    const scheduleType = e.target.value as RoundRobinGroupScheduleType;
                    updateRoundRobinGroupStage({
                      schedule_type: scheduleType,
                      matches_per_player: scheduleType === "limited_schedule"
                        ? roundRobin.group_stage?.matches_per_player ?? roundRobin.round_count
                        : null,
                    });
                  }}
                  disabled={saving}
                >
                  <option value="full_round_robin">Todos contra todos</option>
                  <option value="limited_schedule">Calendario limitado</option>
                  <option value="manual_schedule">Manual</option>
                </select>
              </label>
              {roundRobin.group_stage?.schedule_type === "limited_schedule" ? (
                <label>
                  Partidos por jugador
                  <input
                    type="number"
                    min={1}
                    value={roundRobinPhaseDrafts.matches_per_player ?? String(roundRobin.group_stage?.matches_per_player ?? roundRobin.round_count)}
                    onChange={(e) => setRoundRobinPhaseDraft("matches_per_player", e.target.value)}
                    onBlur={() => commitRoundRobinPhaseNumber(
                      "matches_per_player",
                      roundRobin.group_stage?.matches_per_player ?? roundRobin.round_count,
                      1,
                      (value) => {
                        updateRoundRobinPhase({
                          round_count: value,
                          group_stage: { ...roundRobin.group_stage!, matches_per_player: value },
                        });
                      },
                    )}
                    disabled={saving}
                  />
                </label>
              ) : null}
            </div>
            {roundRobinPreview && (
              <div className="format-preview form__full">
                <div className="format-preview__meta">
                  <span className="format-preview__chip">{rrUnit === "equipo" ? "Equipos" : "Jugadores"} <strong>{roundRobinPreview.groups.reduce((sum, group) => sum + group.size, 0)}</strong></span>
                  <span className="format-preview__chip">Partidos de grupos <strong>{roundRobinPreview.totalGroupMatches}</strong></span>
                  <span className="format-preview__chip">Jornadas <strong>{roundRobinPreview.jornadas}</strong></span>
                  {roundRobinPreview.playoffQualifiers ? (
                    <span className="format-preview__chip">Clasifican a playoffs <strong>{roundRobinPreview.playoffQualifiers}</strong></span>
                  ) : null}
                </div>
                <div className="format-preview__groups">
                  {roundRobinPreview.groups.map((group) => (
                    <div key={group.name} className="format-preview__group">
                      <strong>{group.name}</strong>
                      <span>{group.size} {rrUnit}(es)</span>
                      <span>{group.matches} partido(s) · todos vs todos</span>
                    </div>
                  ))}
                </div>
                <p className="format-preview__note format-preview__note--info">
                  {roundRobinPreview.playoffQualifiers
                    ? `Después de grupos: bracket eliminatorio de ${roundRobinPreview.playoffQualifiers} clasificado(s), ${Math.max(roundRobinPreview.playoffQualifiers - 1, 0)} partido(s) hasta el campeón.`
                    : "Sin playoffs: el campeón sale de la tabla general."}
                </p>
              </div>
            )}
            <div className="format-form-section__grid">
              <label>
                Vueltas
                <select className="t123-select"
                  value={roundRobin.rounds_per_pair}
                  onChange={(e) => {
                    const value = (Number(e.target.value) === 2 ? 2 : 1) as 1 | 2;
                    updateRoundRobinPhase({
                      rounds_per_pair: value,
                      group_stage: { ...roundRobin.group_stage!, rounds_per_pair: value },
                    });
                  }}
                  disabled={saving}
                >
                  <option value={1}>Una vuelta</option>
                  <option value={2}>Doble vuelta</option>
                </select>
              </label>
              <label>
                Inicio
                <input
                  type="date"
                  value={roundRobin.start_date}
                  onChange={(e) => updateRoundRobin("start_date", e.target.value)}
                  disabled={saving}
                  required
                />
              </label>
              <label>
                Frecuencia
                <select className="t123-select"
                  value={roundRobin.frequency}
                  onChange={(e) =>
                    updateRoundRobin("frequency", e.target.value as LadderFrequency)
                  }
                  disabled={saving}
                >
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quincenal</option>
                  <option value="monthly">Mensual</option>
                  <option value="custom">Personalizada</option>
                </select>
              </label>
              <label className="format-round-count-field">
                <span>{groupScheduleType === "limited_schedule" ? "# de Jornadas / partidos por jugador" : "# de Jornadas"}</span>
                <input
                  type="number"
                  min={1}
                  value={roundCountDraft ?? String(roundRobin.round_count)}
                  onChange={(e) => setRoundCountDraft(e.target.value)}
                  onBlur={() => {
                    const draft = roundCountDraft;
                    setRoundCountDraft(null);
                    if (draft === null) return;
                    const parsed = Number(draft);
                    const safe =
                      Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
                    if (safe !== roundRobin.round_count) {
                      updateRoundRobinPhase({
                        round_count: safe,
                        group_stage: {
                          ...roundRobin.group_stage!,
                          matches_per_player: groupScheduleType === "limited_schedule" ? safe : roundRobin.group_stage?.matches_per_player ?? null,
                        },
                      });
                    }
                  }}
                  disabled={saving || groupScheduleType === "full_round_robin" || groupScheduleType === "manual_schedule"}
                />
              </label>
              <label>
                Días entre Jornadas
                <input
                  type="number"
                  min={0}
                  value={roundDurationDraft ?? String(roundRobin.round_duration_days)}
                  onChange={(e) => setRoundDurationDraft(e.target.value)}
                  onBlur={() => {
                    const draft = roundDurationDraft;
                    setRoundDurationDraft(null);
                    if (draft === null) return;
                    const parsed = Number(draft);
                    const safe =
                      Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                    if (safe !== roundRobin.round_duration_days) {
                      updateRoundRobin("round_duration_days", safe);
                    }
                  }}
                  disabled={saving || roundRobin.frequency !== "custom"}
                />
              </label>
            </div>
          </div>
            {roundRobin.format_mode === "round_robin_playoffs" ? (
              <>
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Playoffs</h3>
            </div>
                <div className="format-form-section__header format-form-section__header--split format-subsection-header">
                  <div>
                    <h4 className="hint-label">Clasificación a playoffs<FieldHint label="¿Qué es un comodín?">Comodín: además de los que clasifican directo, pasan los mejores de cierto lugar comparando entre todos los grupos (por puntos y desempates).</FieldHint></h4>
                  </div>
                </div>
                <div className="format-form-section__grid playoff-qual-grid">
                  <label>
                    Clasifican de cada grupo: del lugar
                    <input
                      type="number"
                      min={1}
                      value={roundRobinPhaseDrafts.positions_from ?? String(roundRobin.qualification?.rules[0]?.type === "top_n_per_group" ? roundRobin.qualification.rules[0].positions_from : 1)}
                      onChange={(e) => setRoundRobinPhaseDraft("positions_from", e.target.value)}
                      onBlur={() => commitRoundRobinPhaseNumber(
                        "positions_from",
                        roundRobin.qualification?.rules[0]?.type === "top_n_per_group" ? roundRobin.qualification.rules[0].positions_from : 1,
                        1,
                        (value) => updateRoundRobinQualificationRule(0, { positions_from: value }),
                      )}
                      disabled={saving}
                    />
                  </label>
                  <label>
                    al lugar
                    <input
                      type="number"
                      min={1}
                      value={roundRobinPhaseDrafts.positions_to ?? String(roundRobin.qualification?.rules[0]?.type === "top_n_per_group" ? roundRobin.qualification.rules[0].positions_to : 2)}
                      onChange={(e) => setRoundRobinPhaseDraft("positions_to", e.target.value)}
                      onBlur={() => commitRoundRobinPhaseNumber(
                        "positions_to",
                        roundRobin.qualification?.rules[0]?.type === "top_n_per_group" ? roundRobin.qualification.rules[0].positions_to : 2,
                        1,
                        (value) => updateRoundRobinQualificationRule(0, { positions_to: value }),
                      )}
                      disabled={saving}
                    />
                  </label>
                  <label>
                    Además, los mejores de qué lugar (comodines)
                    <input
                      type="number"
                      min={0}
                      value={roundRobinPhaseDrafts.group_position ?? String(roundRobin.qualification?.rules[1]?.type === "best_n_by_group_position" ? roundRobin.qualification.rules[1].group_position : 3)}
                      onChange={(e) => setRoundRobinPhaseDraft("group_position", e.target.value)}
                      onBlur={() => commitRoundRobinPhaseNumber(
                        "group_position",
                        roundRobin.qualification?.rules[1]?.type === "best_n_by_group_position" ? roundRobin.qualification.rules[1].group_position : 3,
                        1,
                        (value) => updateRoundRobinQualificationRule(1, { group_position: value }),
                      )}
                      disabled={saving || wildcardCount === 0}
                    />
                  </label>
                  <label>
                    ¿Cuántos comodines?
                    <input
                      type="number"
                      min={0}
                      value={roundRobinPhaseDrafts.wildcard_count ?? String(wildcardCount)}
                      onChange={(e) => setRoundRobinPhaseDraft("wildcard_count", e.target.value)}
                      onBlur={() => commitRoundRobinPhaseNumber(
                        "wildcard_count",
                        wildcardCount,
                        0,
                        (value) => {
                          if (value === 0) {
                            // 0 wildcards → la posición se va sola a 0.
                            updateRoundRobinQualificationRule(1, { count: 0, group_position: 0 });
                            setRoundRobinPhaseDrafts((current) => {
                              const next = { ...current };
                              delete next.group_position;
                              return next;
                            });
                          } else {
                            const currentPosition = roundRobin.qualification?.rules[1]?.type === "best_n_by_group_position"
                              ? roundRobin.qualification.rules[1].group_position
                              : 0;
                            updateRoundRobinQualificationRule(
                              1,
                              currentPosition > 0 ? { count: value } : { count: value, group_position: 3 },
                            );
                          }
                        },
                      )}
                      disabled={saving}
                    />
                  </label>
                  <label className="fmt-field--full">
                    <span className="hint-label">
                      Cómo se ordenan los seeds de playoffs
                      <FieldHint label="¿Cómo se siembran los clasificados?">
                        «Rendimiento general»: como hasta hoy (los 1.º de grupo arriba si clasifican los primeros de cada grupo). «Por lugar de grupo»: todos los 1.º, luego los 2.º… «Efectividad del torneo»: % de sets ganados (ganados entre jugados) y, si empatan, % de games ganados; un 2.º fuerte puede quedar arriba de un 1.º débil.
                      </FieldHint>
                    </span>
                    <select className="t123-select"
                      value={roundRobin.qualification?.seed_method ?? "overall_performance"}
                      onChange={(e) => updateRoundRobinPhase({
                        qualification: { ...roundRobin.qualification!, seed_method: e.target.value as RoundRobinSeedMethod },
                      })}
                      disabled={saving}
                    >
                      <option value="overall_performance">Rendimiento general (como hoy)</option>
                      <option value="group_position">Por lugar de grupo</option>
                      <option value="effectiveness">Efectividad del torneo (sets %, luego juegos %)</option>
                      {roundRobin.qualification?.seed_method === "manual" ? (
                        <option value="manual">Manual (acomodados en el Draw)</option>
                      ) : null}
                    </select>
                  </label>
                  {wildcardCount > 0 && roundRobin.qualification?.seed_method !== "effectiveness" ? (
                    <label className="fmt-field--full">
                      <span className="hint-label">
                        Cómo se comparan los comodines de distintos grupos
                        <FieldHint label="¿Cómo se eligen los mejores entre grupos?">
                          «Como la tabla»: récord y luego los desempates de la tabla. «Efectividad»: primero el % de sets ganados (ganados entre jugados) y, si empatan, el % de games ganados; es justo aunque los grupos tengan distinto tamaño.
                        </FieldHint>
                      </span>
                      <select className="t123-select"
                        value={roundRobin.qualification?.cross_group_criterion ?? "standings"}
                        onChange={(e) => updateRoundRobinPhase({
                          qualification: {
                            ...roundRobin.qualification!,
                            cross_group_criterion: e.target.value as "standings" | "effectiveness",
                          },
                        })}
                        disabled={saving}
                      >
                        <option value="standings">Como la tabla (récord y desempates)</option>
                        <option value="effectiveness">Efectividad (sets %, luego juegos %)</option>
                      </select>
                    </label>
                  ) : null}
                  <label className="fmt-field--full">
                    <span className="hint-label">
                      Si los clasificados no llenan el cuadro (4, 8, 16…)
                      <FieldHint>
                        Ejemplo: clasifican 6 pero el cuadro es de 8. «Ronda preliminar»: los últimos clasificados juegan un partido extra para completar el cuadro. «Pase directo (BYE)»: los mejores clasificados descansan la primera ronda. «Manual»: tú acomodas el cuadro en el Draw.
                      </FieldHint>
                    </span>
                    <select className="t123-select"
                      value={roundRobin.playoff?.bracket_policy ?? "auto_preliminary"}
                      onChange={(e) => updateRoundRobinPlayoff({ bracket_policy: e.target.value as RoundRobinBracketPolicy })}
                      disabled={saving}
                    >
                      <option value="auto_preliminary">Ronda preliminar (partido extra)</option>
                      <option value="auto_byes">Pase directo (BYE) a los mejores</option>
                      <option value="manual">Manual (yo armo el cuadro)</option>
                    </select>
                  </label>
                  {qualificationSummary.text || qualificationSummary.warnings.length > 0 ? (
                    <div className="fmt-field--full playoff-qual-summary" role="status" aria-live="polite">
                      {qualificationSummary.text ? <p className="playoff-qual-summary__text">{qualificationSummary.text}</p> : null}
                      {qualificationSummary.warnings.map((warning) => (
                        <p key={warning} className="playoff-qual-summary__warn">⚠ {warning}</p>
                      ))}
                    </div>
                  ) : null}
                  <ThirdPlaceCheckbox
                    className="fmt-field--full"
                    checked={roundRobin.playoff?.third_place_match !== false}
                    onChange={(checked) => updateRoundRobinPlayoff({ third_place_match: checked })}
                    disabled={saving}
                  />
                  <label className="form__checkbox form__checkbox--boxed fmt-field--full fmt-avoid-same-group">
                    <input
                      type="checkbox"
                      checked={roundRobin.playoff?.avoid_same_group_first_round !== false}
                      onChange={(e) => updateRoundRobinPlayoff({ avoid_same_group_first_round: e.target.checked })}
                      disabled={saving}
                    />
                    <span>
                      <span className="hint-label">
                        Evitar que se repitan cruces del mismo grupo en la 1.ª ronda
                        <FieldHint label="¿Qué hace esta opción?">
                          Al armar los playoffs, nadie juega su primer partido contra alguien de su propio grupo
                          (cruce clásico 1.º A contra 2.º B). Solo aplica a cuadros nuevos o al rehacer el cuadro.
                        </FieldHint>
                      </span>
                    </span>
                  </label>
                  <ConsolationConfig
                    mode="groups"
                    enabled={roundRobin.consolation_bracket === true}
                    settings={roundRobin.consolation}
                    disabled={saving}
                    tournamentId={tournamentId}
                    onToggle={(checked) =>
                      updateRoundRobinPhase({
                        consolation_bracket: checked,
                        consolation: roundRobin.consolation ?? { ...DEFAULT_CONSOLATION, start: "round_complete" },
                      })
                    }
                    onChange={(next) => updateRoundRobinPhase({ consolation: next })}
                  />
                  {SHOW_SET_FORMAT_FIELDS ? (
                    <label>
                      Formato de sets Playoffs
                      <select className="t123-select"
                        value={roundRobin.playoff?.sets_format ?? roundRobin.playoff_sets_format}
                        onChange={(e) => {
                          const setsFormat = e.target.value as LadderSetFormat;
                          updateRoundRobinPhase({
                            playoff_sets_format: setsFormat,
                            playoff: { ...roundRobin.playoff!, sets_format: setsFormat },
                          });
                        }}
                        disabled={saving}
                      >
                        {SET_FORMAT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {SHOW_SET_FORMAT_FIELDS ? (
                    <label>
                      Tie-break al llegar a games
                      <input
                        type="number"
                        min={4}
                        value={roundRobinPhaseDrafts.playoff_set_tiebreak_games ?? String(roundRobin.playoff?.set_tiebreak_games ?? roundRobin.set_tiebreak_games ?? 6)}
                        onChange={(e) => setRoundRobinPhaseDraft("playoff_set_tiebreak_games", e.target.value)}
                        onBlur={() => commitRoundRobinPhaseNumber(
                          "playoff_set_tiebreak_games",
                          roundRobin.playoff?.set_tiebreak_games ?? roundRobin.set_tiebreak_games ?? 6,
                          4,
                          (value) => updateRoundRobinPlayoff({ set_tiebreak_games: value }),
                        )}
                        disabled={saving}
                      />
                    </label>
                  ) : null}
                  {SHOW_SET_FORMAT_FIELDS ? (
                    <label
                      title={(roundRobin.playoff?.sets_format ?? roundRobin.playoff_sets_format) !== "two_sets_super_tiebreak"
                        ? "Solo aplica cuando el formato de sets de playoffs es «2 y Super Muerte»."
                        : undefined}
                    >
                      Súper muerte a puntos
                      <input
                        type="number"
                        min={7}
                        value={roundRobinPhaseDrafts.playoff_super_tiebreak_points ?? String(roundRobin.playoff?.third_set_super_tiebreak_points ?? roundRobin.third_set_super_tiebreak_points ?? 10)}
                        onChange={(e) => setRoundRobinPhaseDraft("playoff_super_tiebreak_points", e.target.value)}
                        onBlur={() => commitRoundRobinPhaseOptionalNumber(
                          "playoff_super_tiebreak_points",
                          roundRobin.playoff?.third_set_super_tiebreak_points ?? roundRobin.third_set_super_tiebreak_points ?? 10,
                          7,
                          (value) => updateRoundRobinPlayoff({ third_set_super_tiebreak_points: value }),
                        )}
                        // Feedback Eduardo (2026-07-14): sin súper muerte en el
                        // formato (1, 2 de 3, 3 de 5) este campo se bloquea para
                        // no confundir.
                        disabled={saving || (roundRobin.playoff?.sets_format ?? roundRobin.playoff_sets_format) !== "two_sets_super_tiebreak"}
                        placeholder="No aplica"
                      />
                    </label>
                  ) : null}
                </div>
          </div>
          <SpecialScoringSection
            value={roundRobin.special_scoring}
            onChange={(next) => updateRoundRobinPhase({ special_scoring: next })}
            standingsRules={roundRobin.standings_rules}
            onStandingsRulesChange={(next) => updateRoundRobinPhase({ standings_rules: next })}
            teamPlayActive={Boolean(roundRobin.team_play?.enabled)}
            disabled={saving}
          />
          <div className="format-form-section form__full">
                <div className="format-form-section__header">
                  <div>
                    <h3 className="hint-label">Criterios de desempate<FieldHint>Cómo se ordena la tabla y se desempatan los grupos; los sembrados del bracket salen de esta misma escalera.</FieldHint></h3>
                  </div>
                </div>
                <div className="format-form-section__grid">
                  <label>
                    Criterios de desempate
                    <select className="t123-select"
                      value={standingsRulesKind(roundRobin.standings_rules)}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith("preset:")) {
                          const preset = tiebreakPresets[Number(value.slice(7))];
                          if (preset) updateRoundRobinPhase({ standings_rules: customStandingsRules(preset.tiebreakers) });
                          return;
                        }
                        const nextKind = value as StandingsRulesKind;
                        let next: StandingsRulesConfig | null = null;
                        if (nextKind === "itf") {
                          next = itfStandingsRules();
                        } else if (nextKind === "points") {
                          next = pointsLeagueStandingsRules(roundRobin.standings_rules?.points ?? DEFAULT_STANDINGS_POINTS);
                        } else if (nextKind === "custom") {
                          const current = movableTiebreakers(roundRobin.standings_rules);
                          next = customStandingsRules(current.length > 0 ? current : MOVABLE_TIEBREAKER_OPTIONS);
                        }
                        updateRoundRobinPhase({ standings_rules: next });
                      }}
                      disabled={saving}
                    >
                      {standingsRulesKind(roundRobin.standings_rules) === "legacy" && (
                        <option value="legacy">Sistema anterior (torneos existentes)</option>
                      )}
                      <option value="itf">ITF (recomendado)</option>
                      <option value="points">Liga por puntos</option>
                      <option value="custom">Personalizado</option>
                      {tiebreakPresets.length > 0 && (
                        <optgroup label="Usados en tus torneos">
                          {tiebreakPresets.map((preset, index) => (
                            <option key={`${preset.tournament_id}-${index}`} value={`preset:${index}`}>
                              {`Como en ${preset.tournament_name} (personalizado)`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </label>
                  {standingsRulesKind(roundRobin.standings_rules) === "points" && (
                    <>
                      <label>
                        Puntos por victoria
                        <input
                          type="number"
                          min={1}
                          value={roundRobinPhaseDrafts.standings_points_win ?? String(roundRobin.standings_rules?.points?.win ?? DEFAULT_STANDINGS_POINTS.win)}
                          onChange={(e) => setRoundRobinPhaseDraft("standings_points_win", e.target.value)}
                          onBlur={() => commitRoundRobinPhaseNumber(
                            "standings_points_win",
                            roundRobin.standings_rules?.points?.win ?? DEFAULT_STANDINGS_POINTS.win,
                            1,
                            (value) => updateRoundRobinPhase({
                              standings_rules: pointsLeagueStandingsRules({
                                ...(roundRobin.standings_rules?.points ?? DEFAULT_STANDINGS_POINTS),
                                win: value,
                              }),
                            }),
                          )}
                          disabled={saving}
                        />
                      </label>
                      <label>
                        Puntos por completar (aunque pierda)
                        <input
                          type="number"
                          min={0}
                          value={roundRobinPhaseDrafts.standings_points_loss ?? String(roundRobin.standings_rules?.points?.loss_completed ?? DEFAULT_STANDINGS_POINTS.loss_completed)}
                          onChange={(e) => setRoundRobinPhaseDraft("standings_points_loss", e.target.value)}
                          onBlur={() => commitRoundRobinPhaseNumber(
                            "standings_points_loss",
                            roundRobin.standings_rules?.points?.loss_completed ?? DEFAULT_STANDINGS_POINTS.loss_completed,
                            0,
                            (value) => updateRoundRobinPhase({
                              standings_rules: pointsLeagueStandingsRules({
                                ...(roundRobin.standings_rules?.points ?? DEFAULT_STANDINGS_POINTS),
                                loss_completed: value,
                              }),
                            }),
                          )}
                          disabled={saving}
                        />
                      </label>
                      <label>
                        Puntos por W.O. (no presentarse)
                        <input
                          type="number"
                          min={0}
                          value={roundRobinPhaseDrafts.standings_points_wo ?? String(roundRobin.standings_rules?.points?.walkover_loss ?? DEFAULT_STANDINGS_POINTS.walkover_loss)}
                          onChange={(e) => setRoundRobinPhaseDraft("standings_points_wo", e.target.value)}
                          onBlur={() => commitRoundRobinPhaseNumber(
                            "standings_points_wo",
                            roundRobin.standings_rules?.points?.walkover_loss ?? DEFAULT_STANDINGS_POINTS.walkover_loss,
                            0,
                            (value) => updateRoundRobinPhase({
                              standings_rules: pointsLeagueStandingsRules({
                                ...(roundRobin.standings_rules?.points ?? DEFAULT_STANDINGS_POINTS),
                                walkover_loss: value,
                              }),
                            }),
                          )}
                          disabled={saving}
                        />
                      </label>
                      {standingsPointsError && (
                        <p role="alert" className="form-error form__full">
                          {standingsPointsError}
                        </p>
                      )}
                    </>
                  )}
                </div>
                {(() => {
                  const kind = standingsRulesKind(roundRobin.standings_rules);
                  if (kind === "legacy") {
                    return (
                      <p className="format-helper-text">
                        Este torneo usa el sistema de desempates anterior (puntos → victorias → H2H → dif. de sets → dif. de juegos). Elige <strong>ITF (recomendado)</strong> para adoptar la escalera oficial; el orden de la tabla puede cambiar al guardarlo.
                      </p>
                    );
                  }
                  if (kind === "itf" || kind === "points") {
                    return (
                      <div className="format-helper-text">
                        {kind === "points" && (
                          <p>
                            La tabla se ordena por puntos y muestra la columna PTS; los empates se resuelven con la misma escalera ITF:
                          </p>
                        )}
                        {kind === "itf" && (
                          <p>
                            Sin columna de puntos: la tabla ordena y muestra por victorias (W-L). Escalera de desempate:
                          </p>
                        )}
                        {/* Cada paso con su «?» (Eduardo 2026-10-01). */}
                        <ul>
                          {itfLadderSteps(kind).map(({ line, help }, index) => (
                            <li key={line}>
                              {line}{" "}
                              <FieldHint label={`¿Cómo funciona el paso ${index + 1}?`}>{help}</FieldHint>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  const movable = movableTiebreakers(roundRobin.standings_rules);
                  return (
                    <div className="format-helper-text standings-tiebreaker-editor">
                      <p>
                        <strong>Criterio 0 (fijo):</strong> récord de partidos (victorias / % de partidos ganados).{" "}
                        <FieldHint label="¿Qué es el criterio 0?">{RECORD_HELP}</FieldHint>{" "}
                        <strong>H2H (fijo):</strong> aplica cuando quedan 2 empatados y al reducirse un empate a 2.{" "}
                        <FieldHint label="¿Qué es el H2H?">{H2H_HELP}</FieldHint>
                      </p>
                      <ol>
                        {movable.map((criterion, index) => (
                          <li key={criterion}>
                            <span className="hint-label">
                              {TIEBREAKER_LABELS[criterion]}
                              <FieldHint label={`¿Qué es ${TIEBREAKER_LABELS[criterion]}?`}>
                                {TIEBREAKER_HELP[criterion]} {TIEBREAKER_SCOPE_NOTE}
                              </FieldHint>
                            </span>
                            <span className="admin-ranking-actions">
                              <button
                                type="button"
                                className="btn btn--ghost btn--compact"
                                aria-label={`Subir ${TIEBREAKER_LABELS[criterion]}`}
                                onClick={() => updateRoundRobinPhase({
                                  standings_rules: customStandingsRules(moveTiebreaker(movable, index, -1)),
                                })}
                                disabled={saving || index === 0}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--compact"
                                aria-label={`Bajar ${TIEBREAKER_LABELS[criterion]}`}
                                onClick={() => updateRoundRobinPhase({
                                  standings_rules: customStandingsRules(moveTiebreaker(movable, index, 1)),
                                })}
                                disabled={saving || index === movable.length - 1}
                              >
                                ↓
                              </button>
                            </span>
                          </li>
                        ))}
                      </ol>
                      <p>
                        {DETERMINISTIC_CLOSE_NOTE}{" "}
                        <FieldHint label="¿Qué es el cierre fijo?">{CLOSE_HELP}</FieldHint>
                      </p>
                    </div>
                  );
                })()}
          </div>
              </>
            ) : null}
          {SHOW_SET_FORMAT_FIELDS ? (
            <div className="format-form-section form__full">
              <div className="format-form-section__header format-form-section__header--split">
                <div>
                  <h3 className="hint-label">Formato Partidos<FieldHint>Define sets normales o minisets infantiles para la fase de grupos.</FieldHint></h3>
                </div>
                <span className="format-scoring-pill">{scoringSummary(roundRobin)}</span>
              </div>
              <div className="format-form-section__grid format-form-section__grid--scoring">
                <label>
                  # Sets por Partido
                  <select className="t123-select"
                    value={roundRobin.match_sets_format}
                    onChange={(e) =>
                      updateRoundRobin("match_sets_format", e.target.value as LadderSetFormat)
                    }
                    disabled={saving}
                  >
                    {SET_FORMAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Tipo de Set
                  <select className="t123-select"
                    value={roundRobin.set_game_type}
                    onChange={(e) => updateRoundRobin("set_game_type", e.target.value as SetGameType)}
                    disabled={saving}
                  >
                    {SET_GAME_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Set Tie Break
                  <select className="t123-select"
                    value={roundRobin.set_tiebreak_games}
                    onChange={(e) => updateRoundRobin("set_tiebreak_games", Number(e.target.value))}
                    disabled={saving}
                  >
                    {SET_TIEBREAK_GAME_OPTIONS.map((games) => (
                      <option key={games} value={games}>
                        {games} games
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  3er Set
                  <select
                    className="t123-select format-third-set-select"
                    value={roundRobin.third_set_super_tiebreak_points ?? "none"}
                    onChange={(e) =>
                      updateRoundRobin(
                        "third_set_super_tiebreak_points",
                        e.target.value === "none" ? null : Number(e.target.value),
                      )
                    }
                    disabled={saving}
                  >
                    <option value="none">No aplica</option>
                    {SUPER_TIEBREAK_POINTS_OPTIONS.map((points) => (
                      <option key={points} value={points}>
                        Súper muerte a {points} pts
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form__checkbox format-advantage-toggle">
                  <input
                    type="checkbox"
                    checked={roundRobin.with_advantage}
                    onChange={(e) => updateRoundRobin("with_advantage", e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    <span className="hint-label">
                      <strong>Con ventaja</strong>
                      <FieldHint>
                        Activado: juegos con ventaja tradicional. Apagado: juegos sin ventaja / punto decisivo.
                      </FieldHint>
                    </span>
                  </span>
                </label>
              </div>
            </div>
          ) : null}

          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Notas</h3>
            </div>
            <div className="format-form-section__grid">
              <label className="form__full">
                Notas
                <textarea
                  rows={3}
                  value={roundRobin.notes ?? ""}
                  onChange={(e) =>
                    updateRoundRobin("notes", e.target.value || null)
                  }
                  disabled={saving}
                  placeholder="Reglas adicionales para el grupo (opcional)"
                />
              </label>
            </div>
          </div>
        </form>
      )}

      {pendingType === "elimination" && (
        <form
          id="format-config-form"
          className="form form--inline format-grid--compact-form"
          onSubmit={handleEliminationSubmit}
        >
          <TeamPlayFormatSection
            value={elimination.team_play}
            disabled={saving}
            onChange={(next) => updateElimination("team_play", next)}
          />
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Bracket</h3>
            </div>
            <div className="format-form-section__grid">
              <label>
                Tamaño del bracket
                <select className="t123-select"
                  value={elimination.bracket_size}
                  onChange={(e) =>
                    updateElimination("bracket_size", Number(e.target.value))
                  }
                  disabled={saving}
                >
                  {ELIMINATION_BRACKET_SIZES.map((size) => (
                    <option key={size} value={size}>
                      Ronda {size} · {eliminationRoundCount(size)} ronda{eliminationRoundCount(size) === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Rondas del torneo
                <input
                  type="number"
                  value={elimination.bracket_round_count}
                  readOnly
                  disabled
                />
              </label>
              <label>
                # Jugadores BYE en primera ronda
                <input
                  type="number"
                  min={0}
                  max={elimination.bracket_size}
                  value={elimination.first_round_bye_count ?? ""}
                  onChange={(e) =>
                    updateElimination(
                      "first_round_bye_count",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  placeholder={
                    activeParticipantCount && activeParticipantCount > 0
                      ? String(eliminationPlan.directAdvancers)
                      : "0"
                  }
                  disabled={saving}
                  title="Déjalo vacío para calcularlo automático; captura un número para forzar BYE manual."
                />
              </label>
            </div>
          </div>

          {eliminationPreview && (
            <div className="format-preview form__full">
              <div className="format-preview__meta">
                <span className="format-preview__chip">Partidos <strong>{eliminationPreview.totalMatches}</strong></span>
                <span className="format-preview__chip">Rondas <strong>{Math.max(eliminationPreview.rounds.length, 1)}</strong></span>
                <span className="format-preview__chip">Previas <strong>{eliminationPreview.playInMatches}</strong></span>
                <span className="format-preview__chip">Descansos <strong>{eliminationPreview.restingPlayers}</strong></span>
              </div>
              {eliminationPreview.suggestion && (
                <p className={`format-preview__note format-preview__note--${eliminationPreview.suggestion.tone}`}>
                  {eliminationPreview.suggestion.text}
                </p>
              )}
              {eliminationPreview.rounds.length > 0 && elimination.bracket_size <= 64 && (
                <div className="format-preview__viewport">
                  <div className="format-preview__bracket">
                    <EliminationBracket rounds={eliminationPreview.rounds} />
                  </div>
                </div>
              )}
              {elimination.bracket_size > 64 && (
                <p className="format-preview__note format-preview__note--info">
                  Cuadro muy grande para previsualizar completo; el resumen de arriba refleja la configuración.
                </p>
              )}
            </div>
          )}

          {SHOW_SET_FORMAT_FIELDS ? (
            <div className="format-form-section form__full">
              <div className="format-form-section__header format-form-section__header--split">
                <div>
                  <h3 className="hint-label">Formato Partidos<FieldHint>Define sets normales o minisets infantiles para los cruces de eliminatoria.</FieldHint></h3>
                </div>
                <span className="format-scoring-pill">{scoringSummary(elimination)}</span>
              </div>
              <div className="format-form-section__grid format-form-section__grid--scoring">
                <label>
                  # Sets por Partido
                  <select className="t123-select"
                    value={elimination.match_sets_format}
                    onChange={(e) =>
                      updateElimination("match_sets_format", e.target.value as LadderSetFormat)
                    }
                    disabled={saving}
                  >
                    {SET_FORMAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Tipo de Set
                  <select className="t123-select"
                    value={elimination.set_game_type}
                    onChange={(e) => updateElimination("set_game_type", e.target.value as SetGameType)}
                    disabled={saving}
                  >
                    {SET_GAME_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Set Tie Break
                  <select className="t123-select"
                    value={elimination.set_tiebreak_games}
                    onChange={(e) => updateElimination("set_tiebreak_games", Number(e.target.value))}
                    disabled={saving}
                  >
                    {SET_TIEBREAK_GAME_OPTIONS.map((games) => (
                      <option key={games} value={games}>
                        {games} games
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  3er Set
                  <select
                    className="t123-select format-third-set-select"
                    value={elimination.third_set_super_tiebreak_points ?? "none"}
                    onChange={(e) =>
                      updateElimination(
                        "third_set_super_tiebreak_points",
                        e.target.value === "none" ? null : Number(e.target.value),
                      )
                    }
                    disabled={saving}
                  >
                    <option value="none">No aplica</option>
                    {SUPER_TIEBREAK_POINTS_OPTIONS.map((points) => (
                      <option key={points} value={points}>
                        Súper muerte a {points} pts
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form__checkbox format-advantage-toggle">
                  <input
                    type="checkbox"
                    checked={elimination.with_advantage}
                    onChange={(e) => updateElimination("with_advantage", e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    <span className="hint-label">
                      <strong>Con ventaja</strong>
                      <FieldHint>
                        Activado: juegos con ventaja tradicional. Apagado: juegos sin ventaja / punto decisivo.
                      </FieldHint>
                    </span>
                  </span>
                </label>
              </div>
            </div>
          ) : null}
          <div className="format-form-section form__full">
            <div className="format-form-section__header">
              <h3>Sembrado y reglas</h3>
            </div>
            <div className="format-form-section__grid">
              <label>
                <span className="hint-label">
                  Seeding
                  <FieldHint>
                    El sembrado usa el ranking inicial del paso Ranking; Manual respeta tu acomodo del draw.
                  </FieldHint>
                </span>
                <select className="t123-select"
                  value={elimination.seeding_source}
                  onChange={(e) =>
                    updateElimination(
                      "seeding_source",
                      e.target.value as EliminationFormatConfig["seeding_source"],
                    )
                  }
                  disabled={saving}
                >
                  <option value="initial_rank">Ranking inicial</option>
                  {/* En eliminatoria solo aplica el rank INICIAL (pedido Eduardo
                      2026-07-28): "Ranking actual" es una posición viva que no
                      existe antes del torneo. Solo se muestra si una config
                      guardada ya lo trae (legado), para no mutarla en silencio. */}
                  {elimination.seeding_source === "current_rank" && (
                    <option value="current_rank">Ranking actual (legado)</option>
                  )}
                  <option value="manual">Manual</option>
                </select>
              </label>
              <label>
                Enfrentamientos por omisión
                <select className="t123-select"
                  value={elimination.default_generation_mode}
                  onChange={(e) =>
                    updateElimination(
                      "default_generation_mode",
                      e.target.value as EliminationFormatConfig["default_generation_mode"],
                    )
                  }
                  disabled={saving}
                >
                  <option value="ranking">Por ranking</option>
                  <option value="random">Random</option>
                </select>
              </label>
              <ThirdPlaceCheckbox
                checked={elimination.third_place_match !== false}
                onChange={(checked) => updateElimination("third_place_match", checked)}
                disabled={saving}
              />
              <ConsolationConfig
                mode="elimination"
                enabled={elimination.consolation_bracket === true}
                settings={elimination.consolation}
                disabled={saving}
                tournamentId={tournamentId}
                onToggle={(checked) => updateElimination("consolation_bracket", checked)}
                onChange={(next) => updateElimination("consolation", next)}
              />
              <label className="form__full">
                Notas
                <textarea
                  rows={3}
                  value={elimination.notes ?? ""}
                  onChange={(e) =>
                    updateElimination("notes", e.target.value || null)
                  }
                  disabled={saving}
                  placeholder="Reglas adicionales (opcional)"
                />
              </label>
            </div>
          </div>
        </form>
      )}
      </div>
      <aside className="fmt-aside">
        <div className="fmt-summary">
        {pendingType === "ladder" && (
          <>
              <div className="format-summary form__full">
                <strong>Resumen Escalera</strong>
                <ul>
                  <li>
                    El sistema sugiere enfrentamientos usando <strong>{RANKING_SCOPE_LABELS[ladder.ranking_scope]}</strong>; el
                    admin puede editar y publicar cada matchup.
                  </li>
                  <li>
                    Baja después de publicar: <strong>{DROPOUT_POLICY_LABELS[ladder.dropout_policy_after_publish]}</strong> con
                    auditoría y comunicado.
                  </li>
                  <li>
                    {ladder.round_count} jornadas cada {ladder.round_duration_days}{" "}
                    días desde {ladder.start_date}.
                  </li>
                  <li>
                    {/* Eduardo 2026-09-24: el formato de sets es informativo; el resumen lo dice sin cifras. */}
                    Formato de partidos: el admin podrá capturar cualquier resultado en los partidos, agregar, quitar sets y tie-break.
                    {ladder.playoffs_enabled ? (
                      <>
                        {" "}Playoffs: <strong>{ladder.playoff_players_count}</strong> jugadores, <strong>{ladder.playoff_round_count}</strong>{" "}
                        jornadas.
                      </>
                    ) : " Sin playoffs."}
                  </li>
                  <li>
                    Patrón de retos:{" "}
                    {ladder.weekly_patterns
                      .slice(0, 3)
                      .map(
                        (entry) =>
                          `S${entry.week} ${WEEKLY_PATTERN_LABELS[entry.pattern]}${
                            entry.bye ? " · BYE" : ""
                          }`,
                      )
                      .join(" / ")}
                    {ladder.weekly_patterns.length > 3 ? "…" : ""}{" "}
                    <span className="weekly-patterns__bye-count">
                      ({ladder.weekly_patterns.filter((e) => e.bye).length} jornadas con
                      BYE)
                    </span>
                  </li>
                </ul>
              </div>
              <div className="form__actions">
                {renderActions ? null : (
                  <button className="btn fmt-summary__save" type="submit" form="format-config-form" disabled={saving}>
                    {saving
                      ? "Guardando…"
                      : submitLabel ?? "Guardar formato Escalera"}
                  </button>
                )}
                {isDirtyType && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setPendingType(config.format_type)}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                )}
              </div>
          </>
        )}
        {pendingType === "round_robin" && (
          <>
              <div className="format-summary form__full">
                <strong>Resumen Round Robin</strong>
                <ul>
                  <li>
                    Fase de grupos: <strong>{groupScheduleLabel}</strong>. {groupScheduleDescription}
                  </li>
                  <li>
                    {roundRobin.round_count} jornadas cada {roundRobin.round_duration_days}{" "}
                    días desde {roundRobin.start_date}.
                  </li>
                  <li>
                    Camino: <strong>{ROUND_ROBIN_FORMAT_MODE_LABELS[roundRobin.format_mode ?? "round_robin_playoffs"]}</strong>
                    {roundRobin.group_stage
                      ? ` · ${roundRobin.group_stage.group_count} grupos de ${roundRobin.group_stage.players_per_group}`
                      : ""}
                    .
                  </li>
                  {roundRobin.format_mode === "round_robin_playoffs" ? (
                    <li>
                      Clasifican lugares {directPositionsLabel} por grupo + {wildcardLabel}; bracket {" "}
                      <strong>{ROUND_ROBIN_BRACKET_POLICY_LABELS[roundRobin.playoff?.bracket_policy ?? "auto_preliminary"]}</strong>,{" "}
                      <strong>{roundRobin.playoff?.third_place_match !== false ? "con 3er lugar" : "sin 3er lugar"}</strong>
                      {roundRobin.consolation_bracket ? (
                        <>, <strong>{consolationSummary(true, roundRobin.consolation, "groups")}</strong></>
                      ) : null}
                      .
                    </li>
                  ) : null}
                  <li>
                    Formato de partidos: el admin podrá capturar cualquier resultado en los partidos, agregar, quitar sets y tie-break.
                  </li>
                  <li>
                    El Draw genera los enfrentamientos por grupo para el Rol de Juegos.
                  </li>
                </ul>
              </div>
              <div className="form__actions">
                {renderActions ? null : (
                  <button className="btn fmt-summary__save" type="submit" form="format-config-form" disabled={saving}>
                    {saving
                      ? "Guardando…"
                      : submitLabel ?? "Guardar formato Round Robin"}
                  </button>
                )}
              </div>
          </>
        )}
        {pendingType === "elimination" && (
          <>
              <div className="format-summary form__full">
                <strong>Resumen Eliminatoria</strong>
                <ul>
                  <li>
                    Bracket de <strong>Ronda {elimination.bracket_size}</strong> con{" "}
                    <strong>
                      {elimination.seeding_source === "initial_rank"
                        ? "ranking inicial"
                        : elimination.seeding_source === "current_rank"
                          ? "ranking actual"
                          : "asignación manual"}
                    </strong>
                    .
                  </li>
                  <li>
                    Tendrá <strong>{elimination.bracket_round_count}</strong> ronda{elimination.bracket_round_count === 1 ? "" : "s"}
                    {elimination.bracket_size >= 4 ? (
                      <>, <strong>{elimination.third_place_match !== false ? "con 3er lugar" : "sin 3er lugar"}</strong></>
                    ) : null}
                    {elimination.consolation_bracket ? (
                      <>, <strong>{consolationSummary(true, elimination.consolation, "elimination")}</strong></>
                    ) : null}
                    ; los enfrentamientos por omisión son <strong>{elimination.default_generation_mode === "random" ? "random" : "por ranking"}</strong>.
                  </li>
                  <li>
                    Formato de partidos: el admin podrá capturar cualquier resultado en los partidos, agregar, quitar sets y tie-break.
                  </li>
                  <li>
                    {activeParticipantCount && activeParticipantCount > 0
                      ? summaryPlayIn > 0
                        ? <>Con <strong>{activeParticipantCount}</strong> {elimUnit}: <strong>{summaryPlayIn}</strong> partido(s) en Preliminar y <strong>{summaryResting}</strong> {elimUnit} pasan directo a Ronda {eliminationPlan.nextRoundSize}{eliminationPlan.hasManualByeCount ? " (BYE manual)." : "."}</>
                        : summaryByesWithinBracket
                          ? <>Con <strong>{activeParticipantCount}</strong> {elimUnit}: sin ronda preliminar — todos entran a Ronda {elimination.bracket_size} y <strong>{summaryResting}</strong> descanso(s) van a los mejores sembrados{eliminationPlan.hasManualByeCount ? " (BYE manual)." : "."}</>
                          : <>Con <strong>{activeParticipantCount}</strong> {elimUnit}: sin ronda preliminar; todos entran directo a Ronda {elimination.bracket_size}{eliminationPlan.hasManualByeCount ? ` (${eliminationPlan.directAdvancers} BYE manual).` : "."}</>
                      : eliminationPlan.hasManualByeCount
                        ? <>BYE manual configurado: <strong>{eliminationPlan.directAdvancers}</strong> {elimUnit}.</>
                        : <>Después de cargar jugadores, aquí se mostrará cuántos juegan Preliminar y cuántos pasan directo.</>}
                  </li>
                </ul>
              </div>
              <div className="form__actions">
                {renderActions ? null : (
                  <button className="btn fmt-summary__save" type="submit" form="format-config-form" disabled={saving}>
                    {saving
                      ? "Guardando…"
                      : submitLabel ?? "Guardar formato Eliminatoria"}
                  </button>
                )}
              </div>
          </>
        )}
        </div>
      </aside>
      </div>
      {renderActions ? renderActions({ saving }) : null}
    </section>
  );
}

function labelFor(format: TournamentType): string {
  return tournamentTypeLabel(format);
}
